import type { AssistantActivitySourceKind, AssistantActivitySourceResult } from '@digital-twins/shared-types/assistant-activity'
import type { AssistantToolName, AssistantStageName } from '@digital-twins/shared-types/assistant'

/**
 * Every key the activity copy can name. The two assistant vocabularies plus
 * `tracker_search`, a display-only target: a `document_search` step that
 * searched trackers expands into it (D8). It has no step or wire name.
 */
export type ActivityTargetKey = AssistantToolName | AssistantStageName | 'tracker_search'

export const ROOM_LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Spanish' },
  { code: 'fr', name: 'French' },
  { code: 'de', name: 'German' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'it', name: 'Italian' },
  { code: 'nl', name: 'Dutch' },
  { code: 'tr', name: 'Turkish' },
  { code: 'ja', name: 'Japanese' },
] as const

export interface ChatPlaceholderTemplates {
  interview: (name: string) => string
  interviewFallback: string
  group: (name: string) => string
  groupFallback: string
  emptyState: string
  /** Headline over the roster while a 1:1 conversation has no messages. */
  emptyHeadlineInterview: (name: string) => string
  /** Headline over the roster while a group conversation has no messages. */
  emptyHeadlineGroup: string
}

export const CHAT_PLACEHOLDERS: Record<string, ChatPlaceholderTemplates> = {
  en: {
    interview: (name) => `Message ${name}...`,
    interviewFallback: 'What would you like to know?',
    group: (name) => `Ask ${name} and others...`,
    groupFallback: 'What would you like to ask the group?',
    emptyState: 'Start the conversation by typing a message below.',
    emptyHeadlineInterview: (name) => `Ask ${name} anything`,
    emptyHeadlineGroup: 'Ask the group anything',
  },
  es: {
    interview: (name) => `Mensaje para ${name}...`,
    interviewFallback: '¿Qué te gustaría saber?',
    group: (name) => `Pregunta a ${name} y otros...`,
    groupFallback: '¿Qué te gustaría preguntarle al grupo?',
    emptyState: 'Inicia la conversación escribiendo un mensaje abajo.',
    emptyHeadlineInterview: (name) => `Pregúntale lo que quieras a ${name}`,
    emptyHeadlineGroup: 'Pregúntale lo que quieras al grupo',
  },
  fr: {
    interview: (name) => `Message à ${name}...`,
    interviewFallback: 'Que souhaitez-vous savoir?',
    group: (name) => `Demandez à ${name} et d'autres...`,
    groupFallback: 'Que souhaitez-vous demander au groupe?',
    emptyState: 'Commencez la conversation en tapant un message ci-dessous.',
    emptyHeadlineInterview: (name) => `Posez n'importe quelle question à ${name}`,
    emptyHeadlineGroup: "Posez n'importe quelle question au groupe",
  },
  de: {
    interview: (name) => `Nachricht an ${name}...`,
    interviewFallback: 'Was möchten Sie wissen?',
    group: (name) => `Fragen Sie ${name} und andere...`,
    groupFallback: 'Was möchten Sie die Gruppe fragen?',
    emptyState: 'Starten Sie das Gespräch, indem Sie unten eine Nachricht eingeben.',
    emptyHeadlineInterview: (name) => `Fragen Sie ${name} alles`,
    emptyHeadlineGroup: 'Fragen Sie die Gruppe alles',
  },
  pt: {
    interview: (name) => `Mensagem para ${name}...`,
    interviewFallback: 'O que você gostaria de saber?',
    group: (name) => `Pergunte a ${name} e outros...`,
    groupFallback: 'O que você gostaria de perguntar ao grupo?',
    emptyState: 'Inicie a conversa digitando uma mensagem abaixo.',
    emptyHeadlineInterview: (name) => `Pergunte o que quiser a ${name}`,
    emptyHeadlineGroup: 'Pergunte o que quiser ao grupo',
  },
  it: {
    interview: (name) => `Messaggio a ${name}...`,
    interviewFallback: 'Cosa vorresti sapere?',
    group: (name) => `Chiedi a ${name} e ad altri...`,
    groupFallback: 'Cosa vorresti chiedere al gruppo?',
    emptyState: 'Avvia la conversazione digitando un messaggio qui sotto.',
    emptyHeadlineInterview: (name) => `Chiedi qualsiasi cosa a ${name}`,
    emptyHeadlineGroup: 'Chiedi qualsiasi cosa al gruppo',
  },
  nl: {
    interview: (name) => `Bericht aan ${name}...`,
    interviewFallback: 'Wat wilt u weten?',
    group: (name) => `Vraag het aan ${name} en anderen...`,
    groupFallback: 'Wat wilt u de groep vragen?',
    emptyState: 'Begin het gesprek door hieronder een bericht te typen.',
    emptyHeadlineInterview: (name) => `Vraag ${name} wat u maar wilt`,
    emptyHeadlineGroup: 'Vraag de groep wat u maar wilt',
  },
  tr: {
    interview: (name) => `${name} kişisine mesaj...`,
    interviewFallback: 'Ne öğrenmek istersiniz?',
    group: (name) => `${name} ve diğerlerine sorun...`,
    groupFallback: 'Gruba ne sormak istersiniz?',
    emptyState: 'Aşağıya bir mesaj yazarak sohbeti başlatın.',
    emptyHeadlineInterview: (name) => `${name} kişisine istediğinizi sorun`,
    emptyHeadlineGroup: 'Gruba istediğinizi sorun',
  },
  ja: {
    interview: (name) => `${name}さんへのメッセージ...`,
    interviewFallback: '何をお聞きになりたいですか？',
    group: (name) => `${name}さんたちに質問する...`,
    groupFallback: 'グループに何をお聞きになりたいですか？',
    emptyState: '下にメッセージを入力して会話を始めましょう。',
    emptyHeadlineInterview: (name) => `${name}さんに何でも聞いてみましょう`,
    emptyHeadlineGroup: 'グループに何でも聞いてみましょう',
  },
}

export function getChatPlaceholders(language: string): ChatPlaceholderTemplates {
  return CHAT_PLACEHOLDERS[language] ?? CHAT_PLACEHOLDERS['en']
}

export interface HomePlaceholderTemplates {
  chat: string
  study: string
  data: string
}

/**
 * Home Lab composer placeholders, one triplet per mode.
 *
 * Chat and Study still name their subject, because both are pointed at a twin
 * audience and the sentence has a "them" to refer to.
 *
 * Data (the Ask mode) no longer does. Its line used to read "…from your
 * documents", from when Ask was library retrieval and nothing else. Ask is now
 * the full assistant: it reads the library, searches the web, runs commands and
 * arms workflows, so naming documents advertised the narrowest of the things it
 * can do and quietly hid the rest. The replacement names no source at all and
 * simply opens the turn. Do not "restore" the documents wording; it would be a
 * smaller promise than the mode keeps.
 */
export const HOME_PLACEHOLDERS: Record<string, HomePlaceholderTemplates> = {
  en: {
    chat: 'What do you want to ask them?',
    study: 'What do you want to learn from them?',
    data: 'How can I help?',
  },
  es: {
    chat: '¿Qué quieres preguntarles?',
    study: '¿Qué quieres aprender de ellos?',
    data: '¿En qué puedo ayudarte?',
  },
  fr: {
    chat: 'Que voulez-vous leur demander?',
    study: "Que voulez-vous apprendre d'eux?",
    data: 'Comment puis-je vous aider?',
  },
  de: {
    chat: 'Was möchten Sie sie fragen?',
    study: 'Was möchten Sie von ihnen erfahren?',
    data: 'Wie kann ich Ihnen helfen?',
  },
  pt: {
    chat: 'O que você quer perguntar a eles?',
    study: 'O que você quer aprender com eles?',
    data: 'Como posso ajudar?',
  },
  it: {
    chat: 'Cosa vuoi chiedere loro?',
    study: 'Cosa vuoi imparare da loro?',
    data: 'Come posso aiutarti?',
  },
  nl: {
    chat: 'Wat wilt u hen vragen?',
    study: 'Wat wilt u van hen leren?',
    data: 'Waarmee kan ik u helpen?',
  },
  tr: {
    chat: 'Onlara ne sormak istersiniz?',
    study: 'Onlardan ne öğrenmek istersiniz?',
    data: 'Size nasıl yardımcı olabilirim?',
  },
  ja: {
    chat: '彼らに何を聞きたいですか？',
    study: '彼らから何を学びたいですか？',
    data: '何をお手伝いしましょうか？',
  },
}

export function getHomePlaceholders(language: string): HomePlaceholderTemplates {
  return HOME_PLACEHOLDERS[language] ?? HOME_PLACEHOLDERS['en']
}

/**
 * Plural-safe template set, keyed by `Intl.PluralRules` category. Only `other`
 * is required: a language that does not inflect a category (Japanese has no
 * `one`) lists `other` alone, and a missing category falls back to it.
 * `{count}` is replaced with the formatted number.
 */
export interface ThreadPluralTemplates {
  zero?: string
  one?: string
  two?: string
  few?: string
  many?: string
  other: string
}

/**
 * Result units whose count the activity row never prints. A11 D7: a web search
 * reads "Searched the web" with no number (it looks at many pages and keeps a
 * few, so any page count misleads); the count stays in telemetry only.
 */
const UNCOUNTED_ACTIVITY_UNITS = ['web_page'] as const

export type CountedActivityUnit = Exclude<
  AssistantActivitySourceResult['unit'],
  (typeof UNCOUNTED_ACTIVITY_UNITS)[number]
>

export function isCountedActivityUnit(
  unit: AssistantActivitySourceResult['unit'],
): unit is CountedActivityUnit {
  return !(UNCOUNTED_ACTIVITY_UNITS as ReadonlyArray<string>).includes(unit)
}

/**
 * The activity checklist (`ThreadActivitySteps`): one row per step a turn
 * actually ran (docs/plans/thread-activity-steps.md, D1, D2, D9).
 *
 * `running` and `done` are complete verb phrases WITHOUT an object, so a row
 * still reads as a sentence when nothing can name the object (D5). Running
 * rows get the ellipsis appended in code, so no template carries one.
 * `withObject` and `withQuery` place the resolved name or the model's quoted
 * lookup in the language's own order; `{step}` is the verb phrase.
 *
 * Tone (D9): a plain verb and an object, the way an analyst's notes read. No
 * "for you", no exclamation, no internal words. A failed step is left out of
 * the list; the answer says what could not be read.
 */
export interface ThreadStepTemplates {
  sourceNames: Record<AssistantActivitySourceKind, string>
  sourceSearch: { running: string; done: string }
  resultNames: Record<AssistantActivitySourceResult['unit'], string>
  resultCounts: Record<CountedActivityUnit, ThreadPluralTemplates>
  sourceUnavailable: string
  sourceEmpty: string
  running: Record<AssistantToolName | AssistantStageName, string>
  done: Record<AssistantToolName | AssistantStageName, string>
  /**
   * Social rows word the name into the sentence, in `{object}`. Optional: a
   * language without it keeps the verb phrase plus `withObject`. Terms reuse
   * that language's existing rows and `sourceNames.social`.
   */
  social?: {
    /** The whole running row, whichever social tool it is. */
    running: string
    runningUnnamed: string
    done: Partial<Record<AssistantToolName, string>>
  }
  /** `{step}` plus a resolved display name in `{object}`. */
  withObject: string
  /** `{step}` plus the already quoted search text in `{query}`. */
  withQuery: string
  /** Wraps the model's literal text in `{text}` with the language's quotes. */
  quote: string
  /** Result count for search steps only (D4). Never used for zero. */
  results: ThreadPluralTemplates
  /** A search that came back empty. */
  nothingRelevant: string
  /** A step whose `tool_end` reported `ok: false` (D9). */
  unavailable: string
  /** The folded line: how many steps the turn ran (D8). */
  summary: ThreadPluralTemplates
  /** The folded line: how many of them were unavailable (D8). */
  summaryUnavailable: ThreadPluralTemplates
  /** Rows past the visible cap (F14). */
  more: string
  /** A running step this build has no copy for, so a raw tool name never prints. */
  unknownRunning: string
  /** The finished form of `unknownRunning`. */
  unknownDone: string
}

/**
 * The conversation launch card's fixed copy (`ThreadConversationLaunchCard`): thread chrome, so it
 * follows the thread language like the composer. `twins` and `rate` are plural-safe with
 * `{count}`. The product term twin stays English in every language except French.
 */
export interface ConversationLaunchTemplates {
  start: string
  review: string
  open: string
  twins: ThreadPluralTemplates
  rate: ThreadPluralTemplates
  noTwins: string
  startFailed: string
  roomsUnavailable: string
}

export interface ThreadPlaceholderTemplates {
  /** The follow-up composer under a thread. */
  askNext: string
  /**
   * The same composer while a REVISABLE gate is docked on it, where a send is
   * routed into the gate as a revision rather than asked as a new question.
   * Gated on the published revision handle, not on the dock: a viewer who does
   * not own the run sees the card but their send still reaches the assistant,
   * so they keep `askNext` and are not promised an edit they cannot make.
   */
  askChanges: string
  /**
   * The wait before any tool has reported in, and the fallback whenever the
   * running tools are ones this build has no name for. It claims nothing beyond
   * "the turn is alive", which is the only thing true in every case.
   */
  working: string
  /**
   * The retrieval phase. `{targets}` is replaced with the RUNNING tools' targets
   * below, joined in the thread's own language. Spoken on the waiting row
   * (`aria-label`); the visible row uses `searchingLead` plus chips instead.
   */
  searching: string
  /**
   * Same phase as `searching`, when at least one running named tool carried a
   * `query`. `{query}` is the model's own string (clipped), not translated.
   */
  searchingFor: string
  /**
   * Visible verb ahead of the source chips. No `{targets}` and no query: the
   * chips carry which sources are running, and a long lookup sits on the chip
   * tooltip rather than in this word.
   */
  searchingLead: string
  /** Every tool has reported back and the answer has not started arriving yet. */
  composing: string
  /** A fairgen card or KPI fence is on screen but has not parsed yet. */
  preparingCanvas: string
  /** Workflow start race: create has been posted, run row not yet on the client. */
  runStarting: string
  /** Active `prompt` step is running (e.g. drafting questions). */
  runPreparing: string
  /** Active `quick_study` step is running or waiting on the external study. */
  runSampling: string
  /** Active `report` step is running. */
  runWriting: string
  /** Active `slack_webhook` step is running. */
  runPosting: string
  /**
   * What each tool is working through, as the fragment `searching` interpolates.
   * Keyed by the Python-owned tool and stage vocabulary. Every entry is required
   * in each language so a newly offered tool cannot silently lose its label.
   *
   * A tool absent from this map is not named out loud. The stream does carry a
   * server `label`, but it is English-only, so borrowing it would put an English
   * fragment inside a Portuguese sentence. An unnamed tool falls back to
   * `working` instead, which stays true whatever is running.
   */
  targets: Record<ActivityTargetKey, string>
  /**
   * Noun on the citation-style chip. Separate from `targets` because those
   * fragments carry sentence prepositions (`the web`, `en la web`) that read
   * wrong inside a pill.
   */
  chips: Record<ActivityTargetKey, string>
  /** The activity checklist and its folded line. */
  steps: ThreadStepTemplates
  /** The conversation launch card. */
  conversationLaunch: ConversationLaunchTemplates
}

const THREAD_STEPS_EN: ThreadStepTemplates = {
  sourceNames: {
    "library": "your library",
    "social": "social listening",
    "study": "your studies",
    "room": "your conversations",
    "audience": "your audiences",
    "web": "the web",
    "dataset": "your datasets",
    "connector_record": "connected sources"
  },
  sourceSearch: {
    "running": "Searching {sources}",
    "done": "Searched {sources}"
  },
  resultNames: {
    "study": "studies",
    "conversation": "conversations",
    "document": "documents",
    "social_digest": "social digests",
    "post": "posts",
    "web_page": "web pages",
    "related_audience": "related audiences",
    "dataset": "datasets",
    "connector_record": "connected records"
  },
  resultCounts: {
    "study": {
      "one": "{count} study",
      "other": "{count} studies"
    },
    "conversation": {
      "one": "{count} conversation",
      "other": "{count} conversations"
    },
    "document": {
      "one": "{count} document",
      "other": "{count} documents"
    },
    "social_digest": {
      "one": "{count} social digest",
      "other": "{count} social digests"
    },
    "post": {
      "one": "{count} post",
      "other": "{count} posts"
    },
    "related_audience": {
      "one": "{count} related audience",
      "other": "{count} related audiences"
    },
    "dataset": {
      "one": "{count} dataset",
      "other": "{count} datasets"
    },
    "connector_record": {
      "one": "{count} connected record",
      "other": "{count} connected records"
    }
  },
  sourceUnavailable: "{source}: unavailable",
  sourceEmpty: "{source}: no matches",
  running: {
    analyze_results: 'Analyzing the results',
    prepare_quick_study: 'Preparing the quick study',
    revise_run_step: 'Revising the study',
    propose_social_tracker: 'Setting up social listening',
    read_room: 'Reading the room conversation',
    read_study: 'Reading the study',
    ask_study: 'Analyzing the study',
    read_document: 'Reading the document',
    list_datasets: 'Listing data files',
    read_tracker_posts: 'Reading social posts',
    analyze_table: 'Analyzing the data',
    report_reading: 'Reading the sources',
    report_writing: 'Writing the report',
    report_checking: 'Checking the report',
    document_search: 'Searching your library',
    web_search: 'Searching the web',
    read_skill: 'Reading a skill',
    read_workflow: 'Reading the workflow',
    read_audience: 'Reading the audience',
    analyze_audience: 'Analyzing the audience',
    read_results: 'Reading the results',
    read_artifact: 'Reading the artifact',
    update_artifact: 'Updating the artifact',
    create_artifact: 'Creating an artifact',
    delete_artifact: 'Deleting the artifact',
    run_workflow: 'Preparing the workflow',
    research_search: 'Searching your research',
    analyze_tracker_posts: 'Analyzing tracker posts',
    generate_image: 'Preparing an image request',
    write_report: 'Starting the report',
    prepare_study: 'Preparing a study',
    search_connected_sources: 'Looking up connected records',
    call_connected_source: 'Reading a connected record',
    read_social_topic: 'Reading social topics',
    analyze_social_topic: 'Analyzing a social topic',
    search_social_posts: 'Searching social posts',
    read_social_post: 'Reading a social post',
    prepare_conversation: 'Preparing a conversation',
  },
  done: {
    analyze_results: 'Analyzed the results',
    prepare_quick_study: 'Prepared the quick study',
    revise_run_step: 'Revised the study',
    propose_social_tracker: 'Set up social listening',
    read_room: 'Read the room conversation',
    read_study: 'Read the study',
    ask_study: 'Analyzed the study',
    read_document: 'Read the document',
    list_datasets: 'Listed data files',
    read_tracker_posts: 'Read social posts',
    analyze_table: 'Analyzed the data',
    report_reading: 'Read the sources',
    report_writing: 'Wrote the report',
    report_checking: 'Checked the report',
    document_search: 'Searched your library',
    web_search: 'Searched the web',
    read_skill: 'Read a skill',
    read_workflow: 'Read the workflow',
    read_audience: 'Read the audience',
    analyze_audience: 'Analyzed the audience',
    read_results: 'Read the results',
    read_artifact: 'Read the artifact',
    update_artifact: 'Updated the artifact',
    create_artifact: 'Created an artifact',
    delete_artifact: 'Deleted the artifact',
    run_workflow: 'Prepared the workflow',
    research_search: 'Searched your research',
    analyze_tracker_posts: 'Analyzed tracker posts',
    generate_image: 'Image request prepared',
    write_report: 'Started the report',
    prepare_study: 'Prepared a study',
    search_connected_sources: 'Looked up connected records',
    call_connected_source: 'Read a connected record',
    read_social_topic: 'Read social topics',
    analyze_social_topic: 'Analyzed a social topic',
    search_social_posts: 'Searched social posts',
    read_social_post: 'Read a social post',
    prepare_conversation: 'Prepared a conversation',
  },
  social: {
    running: 'Checking {object}',
    runningUnnamed: 'Checking social listening',
    done: {
      read_social_topic: 'Read {object}',
      analyze_social_topic: 'Analyzed {object}',
      search_social_posts: 'Searched {object} posts',
      read_social_post: 'Read a {object} post',
      read_tracker_posts: 'Read {object} posts',
      analyze_tracker_posts: 'Analyzed {object} posts',
    },
  },
  withObject: '{step} {object}',
  withQuery: '{step} for {query}',
  quote: '"{text}"',
  results: { one: '{count} result', other: '{count} results' },
  nothingRelevant: 'nothing relevant',
  unavailable: 'unavailable',
  summary: { one: '{count} step', other: '{count} steps' },
  summaryUnavailable: { other: '{count} unavailable' },
  more: '+{count} more',
  unknownRunning: 'Working on a step',
  unknownDone: 'Finished a step',
}

const THREAD_STEPS_ES: ThreadStepTemplates = {
  sourceNames: {
    "library": "tu biblioteca",
    "social": "la escucha social",
    "study": "tus estudios",
    "room": "tus conversaciones",
    "audience": "tus audiencias",
    "web": "la web",
    "dataset": "tus conjuntos de datos",
    "connector_record": "las fuentes conectadas"
  },
  sourceSearch: {
    "running": "Buscando en {sources}",
    "done": "Búsqueda realizada en {sources}"
  },
  resultNames: {
    "study": "estudios",
    "conversation": "conversaciones",
    "document": "documentos",
    "social_digest": "resúmenes sociales",
    "post": "publicaciones",
    "web_page": "páginas web",
    "related_audience": "audiencias relacionadas",
    "dataset": "conjuntos de datos",
    "connector_record": "registros conectados"
  },
  resultCounts: {
    "study": {
      "one": "{count} estudio",
      "other": "{count} estudios"
    },
    "conversation": {
      "one": "{count} conversación",
      "other": "{count} conversaciones"
    },
    "document": {
      "one": "{count} documento",
      "other": "{count} documentos"
    },
    "social_digest": {
      "one": "{count} resumen social",
      "other": "{count} resúmenes sociales"
    },
    "post": {
      "one": "{count} publicación",
      "other": "{count} publicaciones"
    },
    "related_audience": {
      "one": "{count} audiencia relacionada",
      "other": "{count} audiencias relacionadas"
    },
    "dataset": {
      "one": "{count} conjunto de datos",
      "other": "{count} conjuntos de datos"
    },
    "connector_record": {
      "one": "{count} registro conectado",
      "other": "{count} registros conectados"
    }
  },
  sourceUnavailable: "{source}: no disponible",
  sourceEmpty: "{source}: sin coincidencias",
  running: {
    analyze_results: 'Analizando los resultados',
    prepare_quick_study: 'Preparando el estudio rápido',
    revise_run_step: 'Revisando el estudio',
    propose_social_tracker: 'Preparando la escucha social',
    read_room: 'Leyendo la conversación de la sala',
    read_study: 'Leyendo el estudio',
    ask_study: 'Analizando el estudio',
    read_document: 'Leyendo el documento',
    list_datasets: 'Buscando archivos de datos',
    read_tracker_posts: 'Leyendo publicaciones sociales',
    analyze_table: 'Analizando los datos',
    report_reading: 'Leyendo las fuentes',
    report_writing: 'Escribiendo el informe',
    report_checking: 'Revisando el informe',
    document_search: 'Buscando en tu biblioteca',
    web_search: 'Buscando en la web',
    read_skill: 'Leyendo una skill',
    read_workflow: 'Leyendo el flujo',
    read_audience: 'Leyendo la audiencia',
    analyze_audience: 'Analizando la audiencia',
    read_results: 'Leyendo los resultados',
    read_artifact: 'Leyendo el artefacto',
    update_artifact: 'Actualizando el artefacto',
    create_artifact: 'Creando un artefacto',
    delete_artifact: 'Eliminando el artefacto',
    run_workflow: 'Preparando el flujo',
    research_search: 'Buscando en tu investigación',
    analyze_tracker_posts: 'Analizando publicaciones de rastreadores',
    generate_image: 'Preparando una solicitud de imagen',
    write_report: 'Iniciando el informe',
    prepare_study: 'Preparando un estudio',
    search_connected_sources: 'Buscando registros conectados',
    call_connected_source: 'Leyendo un registro conectado',
    read_social_topic: 'Leyendo temas sociales',
    analyze_social_topic: 'Analizando un tema social',
    search_social_posts: 'Buscando publicaciones sociales',
    read_social_post: 'Leyendo una publicación social',
    prepare_conversation: 'Preparando una conversación',
  },
  done: {
    analyze_results: 'Analicé los resultados',
    prepare_quick_study: 'Estudio rápido preparado',
    revise_run_step: 'Revisé el estudio',
    propose_social_tracker: 'Preparé la escucha social',
    read_room: 'Leí la conversación de la sala',
    read_study: 'Leí el estudio',
    ask_study: 'Analicé el estudio',
    read_document: 'Leí el documento',
    list_datasets: 'Archivos de datos encontrados',
    read_tracker_posts: 'Leí publicaciones sociales',
    analyze_table: 'Analicé los datos',
    report_reading: 'Leí las fuentes',
    report_writing: 'Escribí el informe',
    report_checking: 'Revisé el informe',
    document_search: 'Busqué en tu biblioteca',
    web_search: 'Busqué en la web',
    read_skill: 'Leí una skill',
    read_workflow: 'Leí el flujo',
    read_audience: 'Leí la audiencia',
    analyze_audience: 'Analicé la audiencia',
    read_results: 'Leí los resultados',
    read_artifact: 'Leí el artefacto',
    update_artifact: 'Actualicé el artefacto',
    create_artifact: 'Creé un artefacto',
    delete_artifact: 'Eliminé el artefacto',
    run_workflow: 'Preparé el flujo',
    research_search: 'Busqué en tu investigación',
    analyze_tracker_posts: 'Analicé publicaciones de rastreadores',
    generate_image: 'Solicitud de imagen preparada',
    write_report: 'Inicié el informe',
    prepare_study: 'Preparé un estudio',
    search_connected_sources: 'Busqué registros conectados',
    call_connected_source: 'Leí un registro conectado',
    read_social_topic: 'Leí temas sociales',
    analyze_social_topic: 'Analicé un tema social',
    search_social_posts: 'Busqué publicaciones sociales',
    read_social_post: 'Leí una publicación social',
    prepare_conversation: 'Preparé una conversación',
  },
  social: {
    running: 'Revisando {object}',
    runningUnnamed: 'Revisando la escucha social',
    done: {
      read_social_topic: 'Leí {object}',
      analyze_social_topic: 'Analicé {object}',
      search_social_posts: 'Busqué publicaciones de {object}',
      read_social_post: 'Leí una publicación de {object}',
      read_tracker_posts: 'Leí publicaciones de {object}',
      analyze_tracker_posts: 'Analicé publicaciones de {object}',
    },
  },
  withObject: '{step} {object}',
  withQuery: '{step} sobre {query}',
  quote: '"{text}"',
  results: { one: '{count} resultado', other: '{count} resultados' },
  nothingRelevant: 'nada relevante',
  unavailable: 'no disponible',
  summary: { one: '{count} paso', other: '{count} pasos' },
  summaryUnavailable: { one: '{count} no disponible', other: '{count} no disponibles' },
  more: '+{count} más',
  unknownRunning: 'Trabajando en un paso',
  unknownDone: 'Paso completado',
}

const THREAD_STEPS_FR: ThreadStepTemplates = {
  sourceNames: {
    "library": "votre bibliothèque",
    "social": "la veille sociale",
    "study": "vos études",
    "room": "vos conversations",
    "audience": "vos audiences",
    "web": "le Web",
    "dataset": "vos jeux de données",
    "connector_record": "les sources connectées"
  },
  sourceSearch: {
    "running": "Recherche dans {sources}",
    "done": "Recherche effectuée dans {sources}"
  },
  resultNames: {
    "study": "études",
    "conversation": "conversations",
    "document": "documents",
    "social_digest": "synthèses sociales",
    "post": "publications",
    "web_page": "pages Web",
    "related_audience": "audiences associées",
    "dataset": "jeux de données",
    "connector_record": "enregistrements connectés"
  },
  resultCounts: {
    "study": {
      "one": "{count} étude",
      "other": "{count} études"
    },
    "conversation": {
      "one": "{count} conversation",
      "other": "{count} conversations"
    },
    "document": {
      "one": "{count} document",
      "other": "{count} documents"
    },
    "social_digest": {
      "one": "{count} synthèse sociale",
      "other": "{count} synthèses sociales"
    },
    "post": {
      "one": "{count} publication",
      "other": "{count} publications"
    },
    "related_audience": {
      "one": "{count} audience associée",
      "other": "{count} audiences associées"
    },
    "dataset": {
      "one": "{count} jeu de données",
      "other": "{count} jeux de données"
    },
    "connector_record": {
      "one": "{count} enregistrement connecté",
      "other": "{count} enregistrements connectés"
    }
  },
  sourceUnavailable: "{source} : indisponible",
  sourceEmpty: "{source} : aucune correspondance",
  running: {
    analyze_results: 'Analyse des résultats',
    prepare_quick_study: 'Préparation de l’étude rapide',
    revise_run_step: 'Révision de l’étude',
    propose_social_tracker: 'Préparation de l’écoute sociale',
    read_room: 'Lecture de la conversation de la salle',
    read_study: 'Lecture de l’étude',
    ask_study: 'Analyse de l’étude',
    read_document: 'Lecture du document',
    list_datasets: 'Recherche des fichiers de données',
    read_tracker_posts: 'Lecture des publications sociales',
    analyze_table: 'Analyse des données',
    report_reading: 'Lecture des sources',
    report_writing: 'Rédaction du rapport',
    report_checking: 'Vérification du rapport',
    document_search: 'Recherche dans votre bibliothèque',
    web_search: 'Recherche sur le web',
    read_skill: 'Lecture d’une skill',
    read_workflow: 'Lecture du flux',
    read_audience: 'Lecture de l’audience',
    analyze_audience: 'Analyse de l’audience',
    read_results: 'Lecture des résultats',
    read_artifact: 'Lecture de l’artefact',
    update_artifact: 'Mise à jour de l’artefact',
    create_artifact: 'Création d’un artefact',
    delete_artifact: 'Suppression de l’artefact',
    run_workflow: 'Préparation du flux',
    research_search: 'Recherche dans vos études',
    analyze_tracker_posts: 'Analyse des publications des trackers',
    generate_image: 'Préparation d’une demande d’image',
    write_report: 'Lancement du rapport',
    prepare_study: 'Préparation d\'une étude',
    search_connected_sources: 'Recherche d\'enregistrements connectés',
    call_connected_source: 'Lecture d\'un enregistrement connecté',
    read_social_topic: 'Lecture des sujets sociaux',
    analyze_social_topic: 'Analyse d\'un sujet social',
    search_social_posts: 'Recherche de publications sociales',
    read_social_post: 'Lecture d\'une publication sociale',
    prepare_conversation: 'Préparation d’une conversation',
  },
  done: {
    analyze_results: 'Résultats analysés',
    prepare_quick_study: 'Étude rapide préparée',
    revise_run_step: 'Étude révisée',
    propose_social_tracker: 'Écoute sociale préparée',
    read_room: 'Conversation de la salle lue',
    read_study: 'Étude lue',
    ask_study: 'Étude analysée',
    read_document: 'Document lu',
    list_datasets: 'Fichiers de données trouvés',
    read_tracker_posts: 'Publications sociales lues',
    analyze_table: 'Données analysées',
    report_reading: 'Sources lues',
    report_writing: 'Rapport rédigé',
    report_checking: 'Rapport vérifié',
    document_search: 'Recherche effectuée dans votre bibliothèque',
    web_search: 'Recherche effectuée sur le web',
    read_skill: 'Skill lue',
    read_workflow: 'Flux lu',
    read_audience: 'Audience lue',
    analyze_audience: 'Audience analysée',
    read_results: 'Résultats lus',
    read_artifact: 'Artefact lu',
    update_artifact: 'Artefact mis à jour',
    create_artifact: 'Artefact créé',
    delete_artifact: 'Artefact supprimé',
    run_workflow: 'Flux préparé',
    research_search: 'Recherche effectuée dans vos études',
    analyze_tracker_posts: 'Publications des trackers analysées',
    generate_image: 'Demande d’image préparée',
    write_report: 'Rapport lancé',
    prepare_study: 'Étude préparée',
    search_connected_sources: 'Enregistrements connectés consultés',
    call_connected_source: 'Enregistrement connecté lu',
    read_social_topic: 'Sujets sociaux lus',
    analyze_social_topic: 'Sujet social analysé',
    search_social_posts: 'Publications sociales recherchées',
    read_social_post: 'Publication sociale lue',
    prepare_conversation: 'Conversation préparée',
  },
  social: {
    running: 'Vérification de {object}',
    runningUnnamed: 'Vérification de la veille sociale',
    done: {
      read_social_topic: 'Lecture de {object}',
      analyze_social_topic: 'Analyse de {object}',
      search_social_posts: 'Recherche dans les publications de {object}',
      read_social_post: 'Lecture d\'une publication de {object}',
      read_tracker_posts: 'Lecture des publications de {object}',
      analyze_tracker_posts: 'Analyse des publications de {object}',
    },
  },
  withObject: '{step} : {object}',
  withQuery: '{step} pour {query}',
  quote: '"{text}"',
  results: { one: '{count} résultat', other: '{count} résultats' },
  nothingRelevant: 'rien de pertinent',
  unavailable: 'indisponible',
  summary: { one: '{count} étape', other: '{count} étapes' },
  summaryUnavailable: { one: '{count} indisponible', other: '{count} indisponibles' },
  more: '+{count} de plus',
  unknownRunning: 'Étape en cours',
  unknownDone: 'Étape terminée',
}

const THREAD_STEPS_DE: ThreadStepTemplates = {
  sourceNames: {
    "library": "Ihrer Bibliothek",
    "social": "Social Listening",
    "study": "Ihren Studien",
    "room": "Ihren Gesprächen",
    "audience": "Ihren Zielgruppen",
    "web": "dem Web",
    "dataset": "Ihren Datensätzen",
    "connector_record": "verbundenen Quellen"
  },
  sourceSearch: {
    "running": "Suche in {sources}",
    "done": "Gesucht in {sources}"
  },
  resultNames: {
    "study": "Studien",
    "conversation": "Gespräche",
    "document": "Dokumente",
    "social_digest": "Social-Media-Zusammenfassungen",
    "post": "Beiträge",
    "web_page": "Webseiten",
    "related_audience": "zugehörige Zielgruppen",
    "dataset": "Datensätze",
    "connector_record": "verbundene Einträge"
  },
  resultCounts: {
    "study": {
      "one": "{count} Studie",
      "other": "{count} Studien"
    },
    "conversation": {
      "one": "{count} Gespräch",
      "other": "{count} Gespräche"
    },
    "document": {
      "one": "{count} Dokument",
      "other": "{count} Dokumente"
    },
    "social_digest": {
      "one": "{count} Social-Media-Zusammenfassung",
      "other": "{count} Social-Media-Zusammenfassungen"
    },
    "post": {
      "one": "{count} Beitrag",
      "other": "{count} Beiträge"
    },
    "related_audience": {
      "one": "{count} zugehörige Zielgruppe",
      "other": "{count} zugehörige Zielgruppen"
    },
    "dataset": {
      "one": "{count} Datensatz",
      "other": "{count} Datensätze"
    },
    "connector_record": {
      "one": "{count} verbundener Eintrag",
      "other": "{count} verbundene Einträge"
    }
  },
  sourceUnavailable: "{source}: nicht verfügbar",
  sourceEmpty: "{source}: keine Treffer",
  running: {
    analyze_results: 'Ergebnisse werden analysiert',
    prepare_quick_study: 'Kurzstudie wird vorbereitet',
    revise_run_step: 'Studie wird überarbeitet',
    propose_social_tracker: 'Social Listening wird vorbereitet',
    read_room: 'Raumgespräch wird gelesen',
    read_study: 'Studie wird gelesen',
    ask_study: 'Studie wird analysiert',
    read_document: 'Dokument wird gelesen',
    list_datasets: 'Datendateien werden aufgelistet',
    read_tracker_posts: 'Social-Beiträge werden gelesen',
    analyze_table: 'Daten werden analysiert',
    report_reading: 'Quellen werden gelesen',
    report_writing: 'Bericht wird geschrieben',
    report_checking: 'Bericht wird geprüft',
    document_search: 'Bibliothek wird durchsucht',
    web_search: 'Web wird durchsucht',
    read_skill: 'Skill wird gelesen',
    read_workflow: 'Workflow wird gelesen',
    read_audience: 'Audience wird gelesen',
    analyze_audience: 'Audience wird analysiert',
    read_results: 'Ergebnisse werden gelesen',
    read_artifact: 'Artefakt wird gelesen',
    update_artifact: 'Artefakt wird aktualisiert',
    create_artifact: 'Artefakt wird erstellt',
    delete_artifact: 'Artefakt wird gelöscht',
    run_workflow: 'Workflow wird vorbereitet',
    research_search: 'Studien werden durchsucht',
    analyze_tracker_posts: 'Tracker-Beiträge werden analysiert',
    generate_image: 'Bild wird erstellt',
    write_report: 'Bericht wird gestartet',
    prepare_study: 'Studie wird vorbereitet',
    search_connected_sources: 'Verbundene Datensätze werden gesucht',
    call_connected_source: 'Verbundener Datensatz wird gelesen',
    read_social_topic: 'Social-Themen werden gelesen',
    analyze_social_topic: 'Social-Thema wird analysiert',
    search_social_posts: 'Social-Beiträge werden durchsucht',
    read_social_post: 'Social-Beitrag wird gelesen',
    prepare_conversation: 'Gespräch wird vorbereitet',
  },
  done: {
    analyze_results: 'Ergebnisse analysiert',
    prepare_quick_study: 'Kurzstudie vorbereitet',
    revise_run_step: 'Studie überarbeitet',
    propose_social_tracker: 'Social Listening vorbereitet',
    read_room: 'Raumgespräch gelesen',
    read_study: 'Studie gelesen',
    ask_study: 'Studie analysiert',
    read_document: 'Dokument gelesen',
    list_datasets: 'Datendateien aufgelistet',
    read_tracker_posts: 'Social-Beiträge gelesen',
    analyze_table: 'Daten analysiert',
    report_reading: 'Quellen gelesen',
    report_writing: 'Bericht geschrieben',
    report_checking: 'Bericht geprüft',
    document_search: 'Bibliothek durchsucht',
    web_search: 'Web durchsucht',
    read_skill: 'Skill gelesen',
    read_workflow: 'Workflow gelesen',
    read_audience: 'Audience gelesen',
    analyze_audience: 'Audience analysiert',
    read_results: 'Ergebnisse gelesen',
    read_artifact: 'Artefakt gelesen',
    update_artifact: 'Artefakt aktualisiert',
    create_artifact: 'Artefakt erstellt',
    delete_artifact: 'Artefakt gelöscht',
    run_workflow: 'Workflow vorbereitet',
    research_search: 'Studien durchsucht',
    analyze_tracker_posts: 'Tracker-Beiträge analysiert',
    generate_image: 'Bild erstellt',
    write_report: 'Bericht gestartet',
    prepare_study: 'Studie vorbereitet',
    search_connected_sources: 'Verbundene Datensätze gesucht',
    call_connected_source: 'Verbundenen Datensatz gelesen',
    read_social_topic: 'Social-Themen gelesen',
    analyze_social_topic: 'Social-Thema analysiert',
    search_social_posts: 'Social-Beiträge durchsucht',
    read_social_post: 'Social-Beitrag gelesen',
    prepare_conversation: 'Gespräch vorbereitet',
  },
  withObject: '{step}: {object}',
  withQuery: '{step} nach {query}',
  quote: '"{text}"',
  results: { one: '{count} Ergebnis', other: '{count} Ergebnisse' },
  nothingRelevant: 'nichts Relevantes',
  unavailable: 'nicht verfügbar',
  summary: { one: '{count} Schritt', other: '{count} Schritte' },
  summaryUnavailable: { other: '{count} nicht verfügbar' },
  more: '+{count} weitere',
  unknownRunning: 'Schritt wird ausgeführt',
  unknownDone: 'Schritt abgeschlossen',
}

const THREAD_STEPS_PT: ThreadStepTemplates = {
  sourceNames: {
    "library": "tua biblioteca",
    "social": "escuta social",
    "study": "teus estudos",
    "room": "tuas conversas",
    "audience": "tuas audiências",
    "web": "Web",
    "dataset": "teus conjuntos de dados",
    "connector_record": "fontes ligadas"
  },
  sourceSearch: {
    "running": "A pesquisar em {sources}",
    "done": "Pesquisa concluída em {sources}"
  },
  resultNames: {
    "study": "estudos",
    "conversation": "conversas",
    "document": "documentos",
    "social_digest": "resumos sociais",
    "post": "publicações",
    "web_page": "páginas Web",
    "related_audience": "audiências relacionadas",
    "dataset": "conjuntos de dados",
    "connector_record": "registos ligados"
  },
  resultCounts: {
    "study": {
      "one": "{count} estudo",
      "other": "{count} estudos"
    },
    "conversation": {
      "one": "{count} conversa",
      "other": "{count} conversas"
    },
    "document": {
      "one": "{count} documento",
      "other": "{count} documentos"
    },
    "social_digest": {
      "one": "{count} resumo social",
      "other": "{count} resumos sociais"
    },
    "post": {
      "one": "{count} publicação",
      "other": "{count} publicações"
    },
    "related_audience": {
      "one": "{count} audiência relacionada",
      "other": "{count} audiências relacionadas"
    },
    "dataset": {
      "one": "{count} conjunto de dados",
      "other": "{count} conjuntos de dados"
    },
    "connector_record": {
      "one": "{count} registo ligado",
      "other": "{count} registos ligados"
    }
  },
  sourceUnavailable: "{source}: indisponível",
  sourceEmpty: "{source}: sem resultados",
  running: {
    analyze_results: 'Analisando os resultados',
    prepare_quick_study: 'Preparando o estudo rápido',
    revise_run_step: 'Revisando o estudo',
    propose_social_tracker: 'Preparando a escuta social',
    read_room: 'Lendo a conversa da sala',
    read_study: 'Lendo o estudo',
    ask_study: 'Analisando o estudo',
    read_document: 'Lendo o documento',
    list_datasets: 'Listando arquivos de dados',
    read_tracker_posts: 'Lendo publicações sociais',
    analyze_table: 'Analisando os dados',
    report_reading: 'Lendo as fontes',
    report_writing: 'Escrevendo o relatório',
    report_checking: 'Revisando o relatório',
    document_search: 'Pesquisando na sua biblioteca',
    web_search: 'Pesquisando na web',
    read_skill: 'Lendo uma skill',
    read_workflow: 'Lendo o fluxo',
    read_audience: 'Lendo a audiência',
    analyze_audience: 'Analisando a audiência',
    read_results: 'Lendo os resultados',
    read_artifact: 'Lendo o artefato',
    update_artifact: 'Atualizando o artefato',
    create_artifact: 'Criando um artefato',
    delete_artifact: 'Excluindo o artefato',
    run_workflow: 'Preparando o fluxo',
    research_search: 'Pesquisando nos seus estudos',
    analyze_tracker_posts: 'Analisando publicações dos rastreadores',
    generate_image: 'Preparando uma solicitação de imagem',
    write_report: 'Iniciando o relatório',
    prepare_study: 'Preparando um estudo',
    search_connected_sources: 'Buscando registros conectados',
    call_connected_source: 'Lendo um registro conectado',
    read_social_topic: 'Lendo tópicos sociais',
    analyze_social_topic: 'Analisando um tópico social',
    search_social_posts: 'Buscando publicações sociais',
    read_social_post: 'Lendo uma publicação social',
    prepare_conversation: 'Preparando uma conversa',
  },
  done: {
    analyze_results: 'Analisei os resultados',
    prepare_quick_study: 'Estudo rápido preparado',
    revise_run_step: 'Revisei o estudo',
    propose_social_tracker: 'Preparei a escuta social',
    read_room: 'Li a conversa da sala',
    read_study: 'Li o estudo',
    ask_study: 'Analisei o estudo',
    read_document: 'Li o documento',
    list_datasets: 'Arquivos de dados listados',
    read_tracker_posts: 'Li publicações sociais',
    analyze_table: 'Analisei os dados',
    report_reading: 'Li as fontes',
    report_writing: 'Escrevi o relatório',
    report_checking: 'Revisei o relatório',
    document_search: 'Pesquisei na sua biblioteca',
    web_search: 'Pesquisei na web',
    read_skill: 'Li uma skill',
    read_workflow: 'Li o fluxo',
    read_audience: 'Li a audiência',
    analyze_audience: 'Analisei a audiência',
    read_results: 'Li os resultados',
    read_artifact: 'Li o artefato',
    update_artifact: 'Atualizei o artefato',
    create_artifact: 'Criei um artefato',
    delete_artifact: 'Excluí o artefato',
    run_workflow: 'Preparei o fluxo',
    research_search: 'Pesquisei nos seus estudos',
    analyze_tracker_posts: 'Analisei publicações dos rastreadores',
    generate_image: 'Solicitação de imagem preparada',
    write_report: 'Iniciei o relatório',
    prepare_study: 'Preparei um estudo',
    search_connected_sources: 'Busquei registros conectados',
    call_connected_source: 'Li um registro conectado',
    read_social_topic: 'Li tópicos sociais',
    analyze_social_topic: 'Analisei um tópico social',
    search_social_posts: 'Busquei publicações sociais',
    read_social_post: 'Li uma publicação social',
    prepare_conversation: 'Preparei uma conversa',
  },
  social: {
    running: 'Verificando {object}',
    runningUnnamed: 'Verificando escuta social',
    done: {
      read_social_topic: 'Li {object}',
      analyze_social_topic: 'Analisei {object}',
      search_social_posts: 'Busquei publicações de {object}',
      read_social_post: 'Li uma publicação de {object}',
      read_tracker_posts: 'Li publicações de {object}',
      analyze_tracker_posts: 'Analisei publicações de {object}',
    },
  },
  withObject: '{step} {object}',
  withQuery: '{step} sobre {query}',
  quote: '"{text}"',
  results: { one: '{count} resultado', other: '{count} resultados' },
  nothingRelevant: 'nada relevante',
  unavailable: 'indisponível',
  summary: { one: '{count} etapa', other: '{count} etapas' },
  summaryUnavailable: { one: '{count} indisponível', other: '{count} indisponíveis' },
  more: '+{count} a mais',
  unknownRunning: 'Trabalhando em uma etapa',
  unknownDone: 'Etapa concluída',
}

const THREAD_STEPS_IT: ThreadStepTemplates = {
  sourceNames: {
    "library": "la tua libreria",
    "social": "il social listening",
    "study": "i tuoi studi",
    "room": "le tue conversazioni",
    "audience": "i tuoi pubblici",
    "web": "il Web",
    "dataset": "i tuoi dataset",
    "connector_record": "le fonti collegate"
  },
  sourceSearch: {
    "running": "Ricerca in {sources}",
    "done": "Ricerca completata in {sources}"
  },
  resultNames: {
    "study": "studi",
    "conversation": "conversazioni",
    "document": "documenti",
    "social_digest": "riepiloghi social",
    "post": "post",
    "web_page": "pagine Web",
    "related_audience": "pubblici correlati",
    "dataset": "dataset",
    "connector_record": "record collegati"
  },
  resultCounts: {
    "study": {
      "one": "{count} studio",
      "other": "{count} studi"
    },
    "conversation": {
      "one": "{count} conversazione",
      "other": "{count} conversazioni"
    },
    "document": {
      "one": "{count} documento",
      "other": "{count} documenti"
    },
    "social_digest": {
      "one": "{count} riepilogo social",
      "other": "{count} riepiloghi social"
    },
    "post": {
      "one": "{count} post",
      "other": "{count} post"
    },
    "related_audience": {
      "one": "{count} pubblico correlato",
      "other": "{count} pubblici correlati"
    },
    "dataset": {
      "one": "{count} dataset",
      "other": "{count} dataset"
    },
    "connector_record": {
      "one": "{count} record collegato",
      "other": "{count} record collegati"
    }
  },
  sourceUnavailable: "{source}: non disponibile",
  sourceEmpty: "{source}: nessun risultato",
  running: {
    analyze_results: 'Sto analizzando i risultati',
    prepare_quick_study: 'Preparazione dello studio rapido',
    revise_run_step: 'Sto rivedendo lo studio',
    propose_social_tracker: 'Sto preparando il social listening',
    read_room: 'Sto leggendo la conversazione della stanza',
    read_study: 'Sto leggendo lo studio',
    ask_study: 'Sto analizzando lo studio',
    read_document: 'Sto leggendo il documento',
    list_datasets: 'Elenco dei file di dati',
    read_tracker_posts: 'Sto leggendo i post social',
    analyze_table: 'Sto analizzando i dati',
    report_reading: 'Sto leggendo le fonti',
    report_writing: 'Sto scrivendo il report',
    report_checking: 'Sto controllando il report',
    document_search: 'Sto cercando nella tua libreria',
    web_search: 'Sto cercando sul web',
    read_skill: 'Sto leggendo una skill',
    read_workflow: 'Sto leggendo il flusso',
    read_audience: 'Sto leggendo l’audience',
    analyze_audience: 'Sto analizzando l’audience',
    read_results: 'Sto leggendo i risultati',
    read_artifact: 'Sto leggendo l’artefatto',
    update_artifact: 'Sto aggiornando l’artefatto',
    create_artifact: 'Sto creando un artefatto',
    delete_artifact: 'Sto eliminando l’artefatto',
    run_workflow: 'Sto preparando il flusso',
    research_search: 'Sto cercando nei tuoi studi',
    analyze_tracker_posts: 'Sto analizzando i post dei tracker',
    generate_image: 'Sto creando un\'immagine',
    write_report: 'Sto avviando il report',
    prepare_study: 'Sto preparando uno studio',
    search_connected_sources: 'Sto cercando record connessi',
    call_connected_source: 'Sto leggendo un record connesso',
    read_social_topic: 'Sto leggendo i temi social',
    analyze_social_topic: 'Sto analizzando un tema social',
    search_social_posts: 'Sto cercando post social',
    read_social_post: 'Sto leggendo un post social',
    prepare_conversation: 'Preparazione di una conversazione',
  },
  done: {
    analyze_results: 'Ho analizzato i risultati',
    prepare_quick_study: 'Studio rapido preparato',
    revise_run_step: 'Ho rivisto lo studio',
    propose_social_tracker: 'Ho preparato il social listening',
    read_room: 'Ho letto la conversazione della stanza',
    read_study: 'Ho letto lo studio',
    ask_study: 'Ho analizzato lo studio',
    read_document: 'Ho letto il documento',
    list_datasets: 'File di dati elencati',
    read_tracker_posts: 'Ho letto i post social',
    analyze_table: 'Ho analizzato i dati',
    report_reading: 'Ho letto le fonti',
    report_writing: 'Ho scritto il report',
    report_checking: 'Ho controllato il report',
    document_search: 'Ho cercato nella tua libreria',
    web_search: 'Ho cercato sul web',
    read_skill: 'Ho letto una skill',
    read_workflow: 'Ho letto il flusso',
    read_audience: 'Ho letto l’audience',
    analyze_audience: 'Ho analizzato l’audience',
    read_results: 'Ho letto i risultati',
    read_artifact: 'Ho letto l’artefatto',
    update_artifact: 'Ho aggiornato l’artefatto',
    create_artifact: 'Ho creato un artefatto',
    delete_artifact: 'Ho eliminato l’artefatto',
    run_workflow: 'Ho preparato il flusso',
    research_search: 'Ho cercato nei tuoi studi',
    analyze_tracker_posts: 'Ho analizzato i post dei tracker',
    generate_image: 'Ho creato un\'immagine',
    write_report: 'Ho avviato il report',
    prepare_study: 'Ho preparato uno studio',
    search_connected_sources: 'Ho cercato i record connessi',
    call_connected_source: 'Ho letto un record connesso',
    read_social_topic: 'Ho letto i temi social',
    analyze_social_topic: 'Ho analizzato un tema social',
    search_social_posts: 'Ho cercato post social',
    read_social_post: 'Ho letto un post social',
    prepare_conversation: 'Conversazione preparata',
  },
  withObject: '{step} {object}',
  withQuery: '{step} per {query}',
  quote: '"{text}"',
  results: { one: '{count} risultato', other: '{count} risultati' },
  nothingRelevant: 'niente di rilevante',
  unavailable: 'non disponibile',
  summary: { one: '{count} passaggio', other: '{count} passaggi' },
  summaryUnavailable: { one: '{count} non disponibile', other: '{count} non disponibili' },
  more: '+{count} altri',
  unknownRunning: 'Passaggio in corso',
  unknownDone: 'Passaggio completato',
}

const THREAD_STEPS_NL: ThreadStepTemplates = {
  sourceNames: {
    "library": "uw bibliotheek",
    "social": "social listening",
    "study": "uw onderzoeken",
    "room": "uw gesprekken",
    "audience": "uw doelgroepen",
    "web": "het web",
    "dataset": "uw datasets",
    "connector_record": "verbonden bronnen"
  },
  sourceSearch: {
    "running": "Zoeken in {sources}",
    "done": "Gezocht in {sources}"
  },
  resultNames: {
    "study": "onderzoeken",
    "conversation": "gesprekken",
    "document": "documenten",
    "social_digest": "sociale samenvattingen",
    "post": "berichten",
    "web_page": "webpagina’s",
    "related_audience": "gerelateerde doelgroepen",
    "dataset": "datasets",
    "connector_record": "verbonden records"
  },
  resultCounts: {
    "study": {
      "one": "{count} onderzoek",
      "other": "{count} onderzoeken"
    },
    "conversation": {
      "one": "{count} gesprek",
      "other": "{count} gesprekken"
    },
    "document": {
      "one": "{count} document",
      "other": "{count} documenten"
    },
    "social_digest": {
      "one": "{count} sociale samenvatting",
      "other": "{count} sociale samenvattingen"
    },
    "post": {
      "one": "{count} bericht",
      "other": "{count} berichten"
    },
    "related_audience": {
      "one": "{count} gerelateerde doelgroep",
      "other": "{count} gerelateerde doelgroepen"
    },
    "dataset": {
      "one": "{count} dataset",
      "other": "{count} datasets"
    },
    "connector_record": {
      "one": "{count} verbonden record",
      "other": "{count} verbonden records"
    }
  },
  sourceUnavailable: "{source}: niet beschikbaar",
  sourceEmpty: "{source}: geen resultaten",
  running: {
    analyze_results: 'Resultaten analyseren',
    prepare_quick_study: 'Snelle studie voorbereiden',
    revise_run_step: 'Studie aanpassen',
    propose_social_tracker: 'Social listening voorbereiden',
    read_room: 'Ruimtegesprek lezen',
    read_study: 'Studie lezen',
    ask_study: 'Studie analyseren',
    read_document: 'Document lezen',
    list_datasets: 'Gegevensbestanden zoeken',
    read_tracker_posts: 'Social posts lezen',
    analyze_table: 'Gegevens analyseren',
    report_reading: 'Bronnen lezen',
    report_writing: 'Rapport schrijven',
    report_checking: 'Rapport controleren',
    document_search: 'Zoeken in uw bibliotheek',
    web_search: 'Zoeken op het web',
    read_skill: 'Skill lezen',
    read_workflow: 'Workflow lezen',
    read_audience: 'Audience lezen',
    analyze_audience: 'Audience analyseren',
    read_results: 'Resultaten lezen',
    read_artifact: 'Artefact lezen',
    update_artifact: 'Artefact bijwerken',
    create_artifact: 'Artefact maken',
    delete_artifact: 'Artefact verwijderen',
    run_workflow: 'Workflow voorbereiden',
    research_search: 'Zoeken in uw onderzoek',
    analyze_tracker_posts: 'Tracker-posts analyseren',
    generate_image: 'Afbeelding maken',
    write_report: 'Rapport starten',
    prepare_study: 'Studie voorbereiden',
    search_connected_sources: 'Verbonden records zoeken',
    call_connected_source: 'Een verbonden record lezen',
    read_social_topic: 'Social topics lezen',
    analyze_social_topic: 'Een social topic analyseren',
    search_social_posts: 'Social posts zoeken',
    read_social_post: 'Een social post lezen',
    prepare_conversation: 'Een gesprek voorbereiden',
  },
  done: {
    analyze_results: 'Resultaten geanalyseerd',
    prepare_quick_study: 'Snelle studie voorbereid',
    revise_run_step: 'Studie aangepast',
    propose_social_tracker: 'Social listening voorbereid',
    read_room: 'Ruimtegesprek gelezen',
    read_study: 'Studie gelezen',
    ask_study: 'Studie geanalyseerd',
    read_document: 'Document gelezen',
    list_datasets: 'Gegevensbestanden gevonden',
    read_tracker_posts: 'Social posts gelezen',
    analyze_table: 'Gegevens geanalyseerd',
    report_reading: 'Bronnen gelezen',
    report_writing: 'Rapport geschreven',
    report_checking: 'Rapport gecontroleerd',
    document_search: 'Gezocht in uw bibliotheek',
    web_search: 'Gezocht op het web',
    read_skill: 'Skill gelezen',
    read_workflow: 'Workflow gelezen',
    read_audience: 'Audience gelezen',
    analyze_audience: 'Audience geanalyseerd',
    read_results: 'Resultaten gelezen',
    read_artifact: 'Artefact gelezen',
    update_artifact: 'Artefact bijgewerkt',
    create_artifact: 'Artefact gemaakt',
    delete_artifact: 'Artefact verwijderd',
    run_workflow: 'Workflow voorbereid',
    research_search: 'Gezocht in uw onderzoek',
    analyze_tracker_posts: 'Tracker-posts geanalyseerd',
    generate_image: 'Afbeelding gemaakt',
    write_report: 'Rapport gestart',
    prepare_study: 'Studie voorbereid',
    search_connected_sources: 'Verbonden records gezocht',
    call_connected_source: 'Verbonden record gelezen',
    read_social_topic: 'Social topics gelezen',
    analyze_social_topic: 'Social topic geanalyseerd',
    search_social_posts: 'Social posts gezocht',
    read_social_post: 'Social post gelezen',
    prepare_conversation: 'Gesprek voorbereid',
  },
  withObject: '{step}: {object}',
  withQuery: '{step} naar {query}',
  quote: '"{text}"',
  results: { one: '{count} resultaat', other: '{count} resultaten' },
  nothingRelevant: 'niets relevants',
  unavailable: 'niet beschikbaar',
  summary: { one: '{count} stap', other: '{count} stappen' },
  summaryUnavailable: { other: '{count} niet beschikbaar' },
  more: '+{count} meer',
  unknownRunning: 'Bezig met een stap',
  unknownDone: 'Stap afgerond',
}

const THREAD_STEPS_TR: ThreadStepTemplates = {
  sourceNames: {
    "library": "kitaplığınız",
    "social": "sosyal dinleme",
    "study": "araştırmalarınız",
    "room": "görüşmeleriniz",
    "audience": "kitleleriniz",
    "web": "web",
    "dataset": "veri kümeleriniz",
    "connector_record": "bağlı kaynaklar"
  },
  sourceSearch: {
    "running": "{sources} içinde aranıyor",
    "done": "{sources} içinde arandı"
  },
  resultNames: {
    "study": "araştırma",
    "conversation": "görüşme",
    "document": "belge",
    "social_digest": "sosyal özet",
    "post": "gönderi",
    "web_page": "web sayfası",
    "related_audience": "ilgili kitle",
    "dataset": "veri kümesi",
    "connector_record": "bağlı kayıt"
  },
  resultCounts: {
    "study": {
      "one": "{count} araştırma",
      "other": "{count} araştırma"
    },
    "conversation": {
      "one": "{count} görüşme",
      "other": "{count} görüşme"
    },
    "document": {
      "one": "{count} belge",
      "other": "{count} belge"
    },
    "social_digest": {
      "one": "{count} sosyal özet",
      "other": "{count} sosyal özet"
    },
    "post": {
      "one": "{count} gönderi",
      "other": "{count} gönderi"
    },
    "related_audience": {
      "one": "{count} ilgili kitle",
      "other": "{count} ilgili kitle"
    },
    "dataset": {
      "one": "{count} veri kümesi",
      "other": "{count} veri kümesi"
    },
    "connector_record": {
      "one": "{count} bağlı kayıt",
      "other": "{count} bağlı kayıt"
    }
  },
  sourceUnavailable: "{source}: kullanılamıyor",
  sourceEmpty: "{source}: eşleşme yok",
  running: {
    analyze_results: 'Sonuçlar analiz ediliyor',
    prepare_quick_study: 'Hızlı çalışma hazırlanıyor',
    revise_run_step: 'Çalışma düzenleniyor',
    propose_social_tracker: 'Sosyal dinleme hazırlanıyor',
    read_room: 'Oda konuşması okunuyor',
    read_study: 'Çalışma okunuyor',
    ask_study: 'Çalışma analiz ediliyor',
    read_document: 'Belge okunuyor',
    list_datasets: 'Veri dosyaları listeleniyor',
    read_tracker_posts: 'Sosyal gönderiler okunuyor',
    analyze_table: 'Veriler analiz ediliyor',
    report_reading: 'Kaynaklar okunuyor',
    report_writing: 'Rapor yazılıyor',
    report_checking: 'Rapor kontrol ediliyor',
    document_search: 'Kitaplığınızda aranıyor',
    web_search: 'Web\'de aranıyor',
    read_skill: 'Skill okunuyor',
    read_workflow: 'İş akışı okunuyor',
    read_audience: 'Kitle okunuyor',
    analyze_audience: 'Kitle analiz ediliyor',
    read_results: 'Sonuçlar okunuyor',
    read_artifact: 'Çıktı okunuyor',
    update_artifact: 'Çıktı güncelleniyor',
    create_artifact: 'Çıktı oluşturuluyor',
    delete_artifact: 'Çıktı siliniyor',
    run_workflow: 'İş akışı hazırlanıyor',
    research_search: 'Araştırmalarınızda aranıyor',
    analyze_tracker_posts: 'Tracker gönderileri analiz ediliyor',
    generate_image: 'Görsel oluşturuluyor',
    write_report: 'Rapor başlatılıyor',
    prepare_study: 'Çalışma hazırlanıyor',
    search_connected_sources: 'Bağlı kayıtlar aranıyor',
    call_connected_source: 'Bağlı kayıt okunuyor',
    read_social_topic: 'Sosyal konular okunuyor',
    analyze_social_topic: 'Sosyal konu analiz ediliyor',
    search_social_posts: 'Sosyal gönderiler aranıyor',
    read_social_post: 'Sosyal gönderi okunuyor',
    prepare_conversation: 'Sohbet hazırlanıyor',
  },
  done: {
    analyze_results: 'Sonuçlar analiz edildi',
    prepare_quick_study: 'Hızlı çalışma hazırlandı',
    revise_run_step: 'Çalışma düzenlendi',
    propose_social_tracker: 'Sosyal dinleme hazırlandı',
    read_room: 'Oda konuşması okundu',
    read_study: 'Çalışma okundu',
    ask_study: 'Çalışma analiz edildi',
    read_document: 'Belge okundu',
    list_datasets: 'Veri dosyaları listelendi',
    read_tracker_posts: 'Sosyal gönderiler okundu',
    analyze_table: 'Veriler analiz edildi',
    report_reading: 'Kaynaklar okundu',
    report_writing: 'Rapor yazıldı',
    report_checking: 'Rapor kontrol edildi',
    document_search: 'Kitaplığınızda arandı',
    web_search: 'Web\'de arandı',
    read_skill: 'Skill okundu',
    read_workflow: 'İş akışı okundu',
    read_audience: 'Kitle okundu',
    analyze_audience: 'Kitle analiz edildi',
    read_results: 'Sonuçlar okundu',
    read_artifact: 'Çıktı okundu',
    update_artifact: 'Çıktı güncellendi',
    create_artifact: 'Çıktı oluşturuldu',
    delete_artifact: 'Çıktı silindi',
    run_workflow: 'İş akışı hazırlandı',
    research_search: 'Araştırmalarınızda arandı',
    analyze_tracker_posts: 'Tracker gönderileri analiz edildi',
    generate_image: 'Görsel oluşturuldu',
    write_report: 'Rapor başlatıldı',
    prepare_study: 'Çalışma hazırlandı',
    search_connected_sources: 'Bağlı kayıtlar arandı',
    call_connected_source: 'Bağlı kayıt okundu',
    read_social_topic: 'Sosyal konular okundu',
    analyze_social_topic: 'Sosyal konu analiz edildi',
    search_social_posts: 'Sosyal gönderiler arandı',
    read_social_post: 'Sosyal gönderi okundu',
    prepare_conversation: 'Sohbet hazırlandı',
  },
  withObject: '{step}: {object}',
  withQuery: '{step}: {query}',
  quote: '"{text}"',
  results: { other: '{count} sonuç' },
  nothingRelevant: 'ilgili bir şey yok',
  unavailable: 'kullanılamıyor',
  summary: { other: '{count} adım' },
  summaryUnavailable: { other: '{count} kullanılamıyor' },
  more: '+{count} daha',
  unknownRunning: 'Bir adım üzerinde çalışılıyor',
  unknownDone: 'Bir adım tamamlandı',
}

const THREAD_STEPS_JA: ThreadStepTemplates = {
  sourceNames: {
    "library": "ライブラリ",
    "social": "ソーシャルリスニング",
    "study": "調査",
    "room": "会話",
    "audience": "オーディエンス",
    "web": "ウェブ",
    "dataset": "データセット",
    "connector_record": "接続済みソース"
  },
  sourceSearch: {
    "running": "{sources}を検索中",
    "done": "{sources}を検索しました"
  },
  resultNames: {
    "study": "件の調査",
    "conversation": "件の会話",
    "document": "件のドキュメント",
    "social_digest": "件のソーシャル要約",
    "post": "件の投稿",
    "web_page": "件のウェブページ",
    "related_audience": "件の関連オーディエンス",
    "dataset": "件のデータセット",
    "connector_record": "件の接続済みレコード"
  },
  resultCounts: {
    "study": {
      "one": "{count}件の調査",
      "other": "{count}件の調査"
    },
    "conversation": {
      "one": "{count}件の会話",
      "other": "{count}件の会話"
    },
    "document": {
      "one": "{count}件のドキュメント",
      "other": "{count}件のドキュメント"
    },
    "social_digest": {
      "one": "{count}件のソーシャル要約",
      "other": "{count}件のソーシャル要約"
    },
    "post": {
      "one": "{count}件の投稿",
      "other": "{count}件の投稿"
    },
    "related_audience": {
      "one": "{count}件の関連オーディエンス",
      "other": "{count}件の関連オーディエンス"
    },
    "dataset": {
      "one": "{count}件のデータセット",
      "other": "{count}件のデータセット"
    },
    "connector_record": {
      "one": "{count}件の接続済みレコード",
      "other": "{count}件の接続済みレコード"
    }
  },
  sourceUnavailable: "{source}：利用できません",
  sourceEmpty: "{source}：一致する結果なし",
  running: {
    analyze_results: '結果を分析しています',
    prepare_quick_study: 'クイック調査を準備しています',
    revise_run_step: '調査を修正しています',
    propose_social_tracker: 'ソーシャルリスニングを準備しています',
    read_room: 'ルームの会話を読んでいます',
    read_study: '調査を読んでいます',
    ask_study: '調査を分析しています',
    read_document: 'ドキュメントを読んでいます',
    list_datasets: 'データファイルを一覧表示しています',
    read_tracker_posts: 'ソーシャル投稿を読んでいます',
    analyze_table: 'データを分析しています',
    report_reading: '情報源を読んでいます',
    report_writing: 'レポートを作成しています',
    report_checking: 'レポートを確認しています',
    document_search: 'ライブラリを検索しています',
    web_search: 'Webを検索しています',
    read_skill: 'スキルを読んでいます',
    read_workflow: 'ワークフローを読んでいます',
    read_audience: 'オーディエンスを読んでいます',
    analyze_audience: 'オーディエンスを分析しています',
    read_results: '結果を読んでいます',
    read_artifact: 'アーティファクトを読んでいます',
    update_artifact: 'アーティファクトを更新しています',
    create_artifact: 'アーティファクトを作成しています',
    delete_artifact: 'アーティファクトを削除しています',
    run_workflow: 'ワークフローを準備しています',
    research_search: 'リサーチを検索しています',
    analyze_tracker_posts: 'トラッカーの投稿を分析しています',
    generate_image: '画像を作成しています',
    write_report: 'レポートを開始しています',
    prepare_study: '調査を準備しています',
    search_connected_sources: '接続済みのレコードを検索しています',
    call_connected_source: '接続済みのレコードを読んでいます',
    read_social_topic: 'ソーシャルトピックを読んでいます',
    analyze_social_topic: 'ソーシャルトピックを分析しています',
    search_social_posts: 'ソーシャル投稿を検索しています',
    read_social_post: 'ソーシャル投稿を読んでいます',
    prepare_conversation: '会話を準備しています',
  },
  done: {
    analyze_results: '結果を分析しました',
    prepare_quick_study: 'クイック調査を準備しました',
    revise_run_step: '調査を修正しました',
    propose_social_tracker: 'ソーシャルリスニングを準備しました',
    read_room: 'ルームの会話を読みました',
    read_study: '調査を読みました',
    ask_study: '調査を分析しました',
    read_document: 'ドキュメントを読みました',
    list_datasets: 'データファイルを一覧表示しました',
    read_tracker_posts: 'ソーシャル投稿を読みました',
    analyze_table: 'データを分析しました',
    report_reading: '情報源を読みました',
    report_writing: 'レポートを作成しました',
    report_checking: 'レポートを確認しました',
    document_search: 'ライブラリを検索しました',
    web_search: 'Webを検索しました',
    read_skill: 'スキルを読みました',
    read_workflow: 'ワークフローを読みました',
    read_audience: 'オーディエンスを読みました',
    analyze_audience: 'オーディエンスを分析しました',
    read_results: '結果を読みました',
    read_artifact: 'アーティファクトを読みました',
    update_artifact: 'アーティファクトを更新しました',
    create_artifact: 'アーティファクトを作成しました',
    delete_artifact: 'アーティファクトを削除しました',
    run_workflow: 'ワークフローを準備しました',
    research_search: 'リサーチを検索しました',
    analyze_tracker_posts: 'トラッカーの投稿を分析しました',
    generate_image: '画像を作成しました',
    write_report: 'レポートを開始しました',
    prepare_study: '調査を準備しました',
    search_connected_sources: '接続済みのレコードを検索しました',
    call_connected_source: '接続済みのレコードを読みました',
    read_social_topic: 'ソーシャルトピックを読みました',
    analyze_social_topic: 'ソーシャルトピックを分析しました',
    search_social_posts: 'ソーシャル投稿を検索しました',
    read_social_post: 'ソーシャル投稿を読みました',
    prepare_conversation: '会話を準備しました',
  },
  withObject: '{step}：{object}',
  withQuery: '{step}：{query}',
  quote: '「{text}」',
  results: { other: '{count}件' },
  nothingRelevant: '関連する結果なし',
  unavailable: '利用できません',
  summary: { other: '{count}ステップ' },
  summaryUnavailable: { other: '{count}件が利用不可' },
  more: '+{count}件',
  unknownRunning: 'ステップを処理しています',
  unknownDone: 'ステップを完了しました',
}

/**
 * Knowledge thread prose, in the language the THREAD answers in
 * (docs/plans/home-lab-composer-language-and-recent-study.md, D3 and D4).
 *
 * The thread already carries its own language, picked once when it was created and
 * persisted so every turn stays in it, so this table needs no new state: it is the same
 * value the answer itself is written in. A Portuguese answer under an English "Ask next"
 * was the mismatch worth closing.
 *
 * D3 still fences this: these are PROSE, the words the reader reads. The scope chip, the
 * item picker and the sources count stay English, because they are product controls and
 * translating them would need an i18n layer that does not exist.
 *
 * The waiting lines are built as a template plus per-tool fragments rather than one
 * finished sentence per situation, so the set of tools a turn can run is free to grow
 * without this table growing a sentence for every combination of them. Each target
 * carries its own preposition, because which one is right is a fact about the language
 * and not about the tool.
 */
export const THREAD_PLACEHOLDERS: Record<string, ThreadPlaceholderTemplates> = {
  en: {
    askNext: 'Ask next\u2026',
    askChanges: 'Ask for changes\u2026',
    steps: THREAD_STEPS_EN,
    conversationLaunch: {
      start: 'Start',
      review: 'Review twins',
      open: 'Open conversation',
      twins: { one: '{count} twin', other: '{count} twins' },
      rate: { one: 'Sends this question. {count} credit per message.', other: 'Sends this question. {count} credits per message.' },
      noTwins: 'No twins match this audience.',
      startFailed: 'Could not start this conversation. Try again.',
      roomsUnavailable: 'Could not check this conversation. Refresh the page to try again.',
    },
    working: 'Looking into this\u2026',
    searching: 'Searching {targets}\u2026',
    searchingFor: 'Searching {targets} for "{query}"\u2026',
    searchingLead: 'Searching',
    composing: 'Writing the answer\u2026',
    preparingCanvas: 'Preparing visual canvas\u2026',
    runStarting: 'Starting\u2026',
    runPreparing: 'Preparing your questions\u2026',
    runSampling: 'Asking your respondents\u2026',
    runWriting: 'Writing\u2026',
    runPosting: 'Posting\u2026',
    targets: {
      analyze_results: 'Results analysis',
      prepare_quick_study: 'Quick study',
      revise_run_step: 'Study changes',
      propose_social_tracker: 'Social listening',
      read_room: 'Room',
      read_study: 'Study',
      ask_study: 'Study analysis',
      read_document: 'Document',
      list_datasets: 'Datasets',
      read_tracker_posts: 'Social posts',
      analyze_table: 'Table analysis',
      report_reading: 'Report sources',
      report_writing: 'Report writing',
      report_checking: 'Report review',
      document_search: 'your library',
      web_search: 'the web',
      read_skill: 'your skills',
      read_workflow: 'your workflows',
      read_audience: 'your audiences',
      analyze_audience: 'your audience analysis',
      read_results: 'your results',
      read_artifact: 'the artifact',
      update_artifact: 'the artifact',
      create_artifact: 'the creatives artifact',
      delete_artifact: 'the artifact',
      run_workflow: 'a workflow',
      research_search: 'your research',
      analyze_tracker_posts: 'your tracker posts',
      generate_image: 'your image',
      write_report: 'Report writing',
      prepare_study: 'a study',
      search_connected_sources: 'connected records',
      call_connected_source: 'a connected record',
      read_social_topic: 'Social topics',
      analyze_social_topic: 'your social topic',
      search_social_posts: 'Social posts',
      read_social_post: 'Social post',
      prepare_conversation: 'a conversation',
      tracker_search: 'your trackers',
    },
    chips: {
      analyze_results: 'Results analysis',
      prepare_quick_study: 'Quick study',
      revise_run_step: 'Study changes',
      propose_social_tracker: 'Social listening',
      read_room: 'Room',
      read_study: 'Study',
      ask_study: 'Study analysis',
      read_document: 'Document',
      list_datasets: 'Datasets',
      read_tracker_posts: 'Social posts',
      analyze_table: 'Table analysis',
      report_reading: 'Report sources',
      report_writing: 'Report writing',
      report_checking: 'Report review',
      document_search: 'Library',
      web_search: 'Web',
      read_skill: 'Skills',
      read_workflow: 'Workflows',
      read_audience: 'Audience',
      analyze_audience: 'Analysis',
      read_results: 'Results',
      read_artifact: 'Artifact',
      update_artifact: 'Artifact',
      create_artifact: 'Creatives',
      delete_artifact: 'Artifact',
      run_workflow: 'Workflow',
      research_search: 'Research',
      analyze_tracker_posts: 'Tracker posts',
      generate_image: 'Image',
      write_report: 'Report writing',
      prepare_study: 'Study',
      search_connected_sources: 'Connected records',
      call_connected_source: 'Connected record',
      read_social_topic: 'Social topics',
      analyze_social_topic: 'Social topic',
      search_social_posts: 'Social posts',
      read_social_post: 'Social post',
      prepare_conversation: 'Conversation',
      tracker_search: 'Trackers',
    },
  },
  es: {
    askNext: 'Pregunta lo siguiente\u2026',
    askChanges: 'Pide cambios\u2026',
    steps: THREAD_STEPS_ES,
    conversationLaunch: {
      start: 'Iniciar',
      review: 'Revisar twins',
      open: 'Abrir conversaci\u00f3n',
      twins: { one: '{count} twin', other: '{count} twins' },
      rate: { one: 'Env\u00eda esta pregunta. {count} cr\u00e9dito por mensaje.', other: 'Env\u00eda esta pregunta. {count} cr\u00e9ditos por mensaje.' },
      noTwins: 'Ning\u00fan twin coincide con esta audiencia.',
      startFailed: 'No se pudo iniciar esta conversaci\u00f3n. Int\u00e9ntalo de nuevo.',
      roomsUnavailable: 'No se pudo comprobar esta conversaci\u00f3n. Actualiza la p\u00e1gina para intentarlo de nuevo.',
    },
    working: 'Trabajando en ello\u2026',
    searching: 'Buscando {targets}\u2026',
    searchingFor: 'Buscando {targets} por "{query}"\u2026',
    searchingLead: 'Buscando',
    composing: 'Escribiendo la respuesta\u2026',
    preparingCanvas: 'Preparando el lienzo visual\u2026',
    runStarting: 'Iniciando\u2026',
    runPreparing: 'Preparando tus preguntas\u2026',
    runSampling: 'Preguntando a tus encuestados\u2026',
    runWriting: 'Escribiendo\u2026',
    runPosting: 'Publicando\u2026',
    targets: {
      analyze_results: 'Análisis de resultados',
      prepare_quick_study: 'Estudio rápido',
      revise_run_step: 'Cambios del estudio',
      propose_social_tracker: 'Escucha social',
      read_room: 'Sala',
      read_study: 'Estudio',
      ask_study: 'Análisis del estudio',
      read_document: 'Documento',
      list_datasets: 'Datos',
      read_tracker_posts: 'Publicaciones sociales',
      analyze_table: 'Análisis de tabla',
      report_reading: 'Fuentes del informe',
      report_writing: 'Redacción del informe',
      report_checking: 'Revisión del informe',
      document_search: 'en tus documentos',
      web_search: 'en la web',
      read_skill: 'en tus skills',
      read_workflow: 'en tus flujos',
      read_audience: 'en tus audiencias',
      analyze_audience: 'en el análisis de audiencia',
      read_results: 'en tus resultados',
      read_artifact: 'el artefacto',
      update_artifact: 'el artefacto',
      create_artifact: 'el artefacto de creatividades',
      delete_artifact: 'el artefacto',
      run_workflow: 'un flujo',
      research_search: 'en tu investigación',
      analyze_tracker_posts: 'en tus publicaciones de rastreadores',
      generate_image: 'tu imagen',
      write_report: 'Redacción del informe',
      prepare_study: 'un estudio',
      search_connected_sources: 'registros conectados',
      call_connected_source: 'un registro conectado',
      read_social_topic: 'Temas sociales',
      analyze_social_topic: 'en tu tema social',
      search_social_posts: 'Publicaciones sociales',
      read_social_post: 'Publicación social',
      prepare_conversation: 'una conversación',
      tracker_search: 'en tus rastreadores',
    },
    chips: {
      analyze_results: 'Análisis de resultados',
      prepare_quick_study: 'Estudio rápido',
      revise_run_step: 'Cambios del estudio',
      propose_social_tracker: 'Escucha social',
      read_room: 'Sala',
      read_study: 'Estudio',
      ask_study: 'Análisis del estudio',
      read_document: 'Documento',
      list_datasets: 'Datos',
      read_tracker_posts: 'Publicaciones sociales',
      analyze_table: 'Análisis de tabla',
      report_reading: 'Fuentes del informe',
      report_writing: 'Redacción del informe',
      report_checking: 'Revisión del informe',
      document_search: 'Biblioteca',
      web_search: 'Web',
      read_skill: 'Skills',
      read_workflow: 'Flujos',
      read_audience: 'Audiencia',
      analyze_audience: 'Análisis',
      read_results: 'Resultados',
      read_artifact: 'Artefacto',
      update_artifact: 'Artefacto',
      create_artifact: 'Creatividades',
      delete_artifact: 'Artefacto',
      run_workflow: 'Flujo',
      research_search: 'Investigación',
      analyze_tracker_posts: 'Publicaciones de rastreadores',
      generate_image: 'Imagen',
      write_report: 'Redacción del informe',
      prepare_study: 'Estudio',
      search_connected_sources: 'Registros conectados',
      call_connected_source: 'Registro conectado',
      read_social_topic: 'Temas sociales',
      analyze_social_topic: 'Tema social',
      search_social_posts: 'Publicaciones sociales',
      read_social_post: 'Publicación social',
      prepare_conversation: 'Conversación',
      tracker_search: 'Rastreadores',
    },
  },
  fr: {
    askNext: 'Posez la question suivante\u2026',
    askChanges: 'Demandez des modifications\u2026',
    steps: THREAD_STEPS_FR,
    conversationLaunch: {
      start: 'D\u00e9marrer',
      review: 'Revoir les jumeaux',
      open: 'Ouvrir la conversation',
      twins: { one: '{count} jumeau', other: '{count} jumeaux' },
      rate: { one: 'Envoie cette question. {count} cr\u00e9dit par message.', other: 'Envoie cette question. {count} cr\u00e9dits par message.' },
      noTwins: 'Aucun jumeau ne correspond \u00e0 cette audience.',
      startFailed: 'Impossible de d\u00e9marrer cette conversation. R\u00e9essayez.',
      roomsUnavailable: 'Impossible de v\u00e9rifier cette conversation. Actualisez la page pour r\u00e9essayer.',
    },
    working: 'Traitement en cours\u2026',
    searching: 'Recherche {targets}\u2026',
    searchingFor: 'Recherche {targets} pour "{query}"\u2026',
    searchingLead: 'Recherche',
    composing: 'R\u00e9daction de la r\u00e9ponse\u2026',
    preparingCanvas: 'Pr\u00e9paration du canevas visuel\u2026',
    runStarting: 'D\u00e9marrage\u2026',
    runPreparing: 'Pr\u00e9paration de vos questions\u2026',
    runSampling: 'Interrogation de vos r\u00e9pondants\u2026',
    runWriting: 'R\u00e9daction\u2026',
    runPosting: 'Publication\u2026',
    targets: {
      analyze_results: 'Analyse des résultats',
      prepare_quick_study: 'Étude rapide',
      revise_run_step: 'Modifications de l’étude',
      propose_social_tracker: 'Écoute sociale',
      read_room: 'Salle',
      read_study: 'Étude',
      ask_study: 'Analyse de l’étude',
      read_document: 'Document',
      list_datasets: 'Données',
      read_tracker_posts: 'Publications sociales',
      analyze_table: 'Analyse du tableau',
      report_reading: 'Sources du rapport',
      report_writing: 'Rédaction du rapport',
      report_checking: 'Révision du rapport',
      document_search: 'dans vos documents',
      web_search: 'sur le web',
      read_skill: 'dans vos skills',
      read_workflow: 'dans vos flux',
      read_audience: 'dans vos audiences',
      analyze_audience: 'dans l\u2019analyse d\u2019audience',
      read_results: 'dans vos r\u00e9sultats',
      read_artifact: 'l\u2019artefact',
      update_artifact: 'l\u2019artefact',
      create_artifact: 'l\u2019artefact cr\u00e9atif',
      delete_artifact: 'l\u2019artefact',
      run_workflow: 'un flux',
      research_search: 'dans vos études',
      analyze_tracker_posts: 'dans les publications de vos trackers',
      generate_image: 'votre image',
      write_report: 'Rédaction du rapport',
      prepare_study: 'une étude',
      search_connected_sources: 'des enregistrements connectés',
      call_connected_source: 'un enregistrement connecté',
      read_social_topic: 'Sujets sociaux',
      analyze_social_topic: 'dans votre sujet social',
      search_social_posts: 'Publications sociales',
      read_social_post: 'Publication sociale',
      prepare_conversation: 'une conversation',
      tracker_search: 'dans vos trackers',
    },
    chips: {
      analyze_results: 'Analyse des résultats',
      prepare_quick_study: 'Étude rapide',
      revise_run_step: 'Modifications de l’étude',
      propose_social_tracker: 'Écoute sociale',
      read_room: 'Salle',
      read_study: 'Étude',
      ask_study: 'Analyse de l’étude',
      read_document: 'Document',
      list_datasets: 'Données',
      read_tracker_posts: 'Publications sociales',
      analyze_table: 'Analyse du tableau',
      report_reading: 'Sources du rapport',
      report_writing: 'Rédaction du rapport',
      report_checking: 'Révision du rapport',
      document_search: 'Biblioth\u00e8que',
      web_search: 'Web',
      read_skill: 'Skills',
      read_workflow: 'Flux',
      read_audience: 'Audience',
      analyze_audience: 'Analyse',
      read_results: 'R\u00e9sultats',
      read_artifact: 'Artefact',
      update_artifact: 'Artefact',
      create_artifact: 'Cr\u00e9atifs',
      delete_artifact: 'Artefact',
      run_workflow: 'Flux',
      research_search: 'Études',
      analyze_tracker_posts: 'Publications des trackers',
      generate_image: 'Image',
      write_report: 'Rédaction du rapport',
      prepare_study: 'Étude',
      search_connected_sources: 'Enregistrements connectés',
      call_connected_source: 'Enregistrement connecté',
      read_social_topic: 'Sujets sociaux',
      analyze_social_topic: 'Sujet social',
      search_social_posts: 'Publications sociales',
      read_social_post: 'Publication sociale',
      prepare_conversation: 'Conversation',
      tracker_search: 'Trackers',
    },
  },
  de: {
    askNext: 'Als N\u00e4chstes fragen\u2026',
    askChanges: '\u00c4nderungen anfragen\u2026',
    steps: THREAD_STEPS_DE,
    conversationLaunch: {
      start: 'Starten',
      review: 'Twins pr\u00fcfen',
      open: 'Gespr\u00e4ch \u00f6ffnen',
      twins: { one: '{count} Twin', other: '{count} Twins' },
      rate: { one: 'Sendet diese Frage. {count} Credit pro Nachricht.', other: 'Sendet diese Frage. {count} Credits pro Nachricht.' },
      noTwins: 'Kein Twin passt zu dieser Zielgruppe.',
      startFailed: 'Das Gespr\u00e4ch konnte nicht gestartet werden. Versuchen Sie es erneut.',
      roomsUnavailable: 'Das Gespr\u00e4ch konnte nicht gepr\u00fcft werden. Laden Sie die Seite neu, um es erneut zu versuchen.',
    },
    working: 'Wird bearbeitet\u2026',
    searching: 'Suche {targets}\u2026',
    searchingFor: 'Suche {targets} nach "{query}"\u2026',
    searchingLead: 'Suche',
    composing: 'Antwort wird geschrieben\u2026',
    preparingCanvas: 'Visuelle \u00dcbersicht wird vorbereitet\u2026',
    runStarting: 'Wird gestartet\u2026',
    runPreparing: 'Fragen werden vorbereitet\u2026',
    runSampling: 'Ihre Befragten werden gefragt\u2026',
    runWriting: 'Wird geschrieben\u2026',
    runPosting: 'Wird gepostet\u2026',
    targets: {
      analyze_results: 'Ergebnisanalyse',
      prepare_quick_study: 'Kurzstudie',
      revise_run_step: 'Studienänderungen',
      propose_social_tracker: 'Social Listening',
      read_room: 'Raum',
      read_study: 'Studie',
      ask_study: 'Studienanalyse',
      read_document: 'Dokument',
      list_datasets: 'Datensätze',
      read_tracker_posts: 'Social-Beiträge',
      analyze_table: 'Tabellenanalyse',
      report_reading: 'Berichtsquellen',
      report_writing: 'Bericht schreiben',
      report_checking: 'Bericht prüfen',
      document_search: 'in Ihren Dokumenten',
      web_search: 'im Web',
      read_skill: 'in Ihren Skills',
      read_workflow: 'in Ihren Workflows',
      read_audience: 'in Ihren Audiences',
      analyze_audience: 'in Ihrer Audience-Analyse',
      read_results: 'in Ihren Ergebnissen',
      read_artifact: 'im Artefakt',
      update_artifact: 'im Artefakt',
      create_artifact: 'im Kreativ-Artefakt',
      delete_artifact: 'im Artefakt',
      run_workflow: 'einen Workflow',
      research_search: 'in Ihren Studien',
      analyze_tracker_posts: 'in Ihren Tracker-Beiträgen',
      generate_image: 'Ihr Bild',
      write_report: 'Bericht schreiben',
      prepare_study: 'eine Studie',
      search_connected_sources: 'verbundene Datensätze',
      call_connected_source: 'einen verbundenen Datensatz',
      read_social_topic: 'Social-Themen',
      analyze_social_topic: 'in Ihrem Social-Thema',
      search_social_posts: 'Social-Beiträge',
      read_social_post: 'Social-Beitrag',
      prepare_conversation: 'ein Gespräch',
      tracker_search: 'in Ihren Trackern',
    },
    chips: {
      analyze_results: 'Ergebnisanalyse',
      prepare_quick_study: 'Kurzstudie',
      revise_run_step: 'Studienänderungen',
      propose_social_tracker: 'Social Listening',
      read_room: 'Raum',
      read_study: 'Studie',
      ask_study: 'Studienanalyse',
      read_document: 'Dokument',
      list_datasets: 'Datensätze',
      read_tracker_posts: 'Social-Beiträge',
      analyze_table: 'Tabellenanalyse',
      report_reading: 'Berichtsquellen',
      report_writing: 'Bericht schreiben',
      report_checking: 'Bericht prüfen',
      document_search: 'Bibliothek',
      web_search: 'Web',
      read_skill: 'Skills',
      read_workflow: 'Workflows',
      read_audience: 'Audience',
      analyze_audience: 'Analyse',
      read_results: 'Ergebnisse',
      read_artifact: 'Artefakt',
      update_artifact: 'Artefakt',
      create_artifact: 'Creatives',
      delete_artifact: 'Artefakt',
      run_workflow: 'Workflow',
      research_search: 'Studien',
      analyze_tracker_posts: 'Tracker-Beiträge',
      generate_image: 'Bild',
      write_report: 'Bericht schreiben',
      prepare_study: 'Studie',
      search_connected_sources: 'Verbundene Datensätze',
      call_connected_source: 'Verbundener Datensatz',
      read_social_topic: 'Social-Themen',
      analyze_social_topic: 'Social-Thema',
      search_social_posts: 'Social-Beiträge',
      read_social_post: 'Social-Beitrag',
      prepare_conversation: 'Gespräch',
      tracker_search: 'Tracker',
    },
  },
  pt: {
    askNext: 'Pergunte a seguir\u2026',
    askChanges: 'Pe\u00e7a altera\u00e7\u00f5es\u2026',
    steps: THREAD_STEPS_PT,
    conversationLaunch: {
      start: 'Iniciar',
      review: 'Revisar twins',
      open: 'Abrir conversa',
      twins: { one: '{count} twin', other: '{count} twins' },
      rate: { one: 'Envia esta pergunta. {count} cr\u00e9dito por mensagem.', other: 'Envia esta pergunta. {count} cr\u00e9ditos por mensagem.' },
      noTwins: 'Nenhum twin corresponde a esta audi\u00eancia.',
      startFailed: 'N\u00e3o foi poss\u00edvel iniciar esta conversa. Tente novamente.',
      roomsUnavailable: 'N\u00e3o foi poss\u00edvel verificar esta conversa. Atualize a p\u00e1gina para tentar novamente.',
    },
    working: 'Trabalhando nisso\u2026',
    searching: 'Pesquisando {targets}\u2026',
    searchingFor: 'Pesquisando {targets} por "{query}"\u2026',
    searchingLead: 'Pesquisando',
    composing: 'Escrevendo a resposta\u2026',
    preparingCanvas: 'Preparando a tela visual\u2026',
    runStarting: 'Iniciando\u2026',
    runPreparing: 'Preparando suas perguntas\u2026',
    runSampling: 'Perguntando aos seus respondentes\u2026',
    runWriting: 'Escrevendo\u2026',
    runPosting: 'Publicando\u2026',
    targets: {
      analyze_results: 'Análise de resultados',
      prepare_quick_study: 'Estudo rápido',
      revise_run_step: 'Alterações do estudo',
      propose_social_tracker: 'Escuta social',
      read_room: 'Sala',
      read_study: 'Estudo',
      ask_study: 'Análise do estudo',
      read_document: 'Documento',
      list_datasets: 'Dados',
      read_tracker_posts: 'Publicações sociais',
      analyze_table: 'Análise da tabela',
      report_reading: 'Fontes do relatório',
      report_writing: 'Redação do relatório',
      report_checking: 'Revisão do relatório',
      document_search: 'nos seus documentos',
      web_search: 'na web',
      read_skill: 'nas suas skills',
      read_workflow: 'nos seus fluxos',
      read_audience: 'nas suas audi\u00eancias',
      analyze_audience: 'na an\u00e1lise da audi\u00eancia',
      read_results: 'nos seus resultados',
      read_artifact: 'no artefato',
      update_artifact: 'no artefato',
      create_artifact: 'no artefato de criativos',
      delete_artifact: 'no artefato',
      run_workflow: 'um fluxo',
      research_search: 'nos seus estudos',
      analyze_tracker_posts: 'nas publicações dos seus rastreadores',
      generate_image: 'sua imagem',
      write_report: 'Redação do relatório',
      prepare_study: 'um estudo',
      search_connected_sources: 'registros conectados',
      call_connected_source: 'um registro conectado',
      read_social_topic: 'Tópicos sociais',
      analyze_social_topic: 'no seu tópico social',
      search_social_posts: 'Publicações sociais',
      read_social_post: 'Publicação social',
      prepare_conversation: 'uma conversa',
      tracker_search: 'nos seus rastreadores',
    },
    chips: {
      analyze_results: 'Análise de resultados',
      prepare_quick_study: 'Estudo rápido',
      revise_run_step: 'Alterações do estudo',
      propose_social_tracker: 'Escuta social',
      read_room: 'Sala',
      read_study: 'Estudo',
      ask_study: 'Análise do estudo',
      read_document: 'Documento',
      list_datasets: 'Dados',
      read_tracker_posts: 'Publicações sociais',
      analyze_table: 'Análise da tabela',
      report_reading: 'Fontes do relatório',
      report_writing: 'Redação do relatório',
      report_checking: 'Revisão do relatório',
      document_search: 'Biblioteca',
      web_search: 'Web',
      read_skill: 'Skills',
      read_workflow: 'Fluxos',
      read_audience: 'Audi\u00eancia',
      analyze_audience: 'An\u00e1lise',
      read_results: 'Resultados',
      read_artifact: 'Artefato',
      update_artifact: 'Artefato',
      create_artifact: 'Criativos',
      delete_artifact: 'Artefato',
      run_workflow: 'Fluxo',
      research_search: 'Estudos',
      analyze_tracker_posts: 'Publicações dos rastreadores',
      generate_image: 'Imagem',
      write_report: 'Redação do relatório',
      prepare_study: 'Estudo',
      search_connected_sources: 'Registros conectados',
      call_connected_source: 'Registro conectado',
      read_social_topic: 'Tópicos sociais',
      analyze_social_topic: 'Tópico social',
      search_social_posts: 'Publicações sociais',
      read_social_post: 'Publicação social',
      prepare_conversation: 'Conversa',
      tracker_search: 'Rastreadores',
    },
  },
  it: {
    askNext: 'Chiedi altro\u2026',
    askChanges: 'Chiedi modifiche\u2026',
    steps: THREAD_STEPS_IT,
    conversationLaunch: {
      start: 'Avvia',
      review: 'Rivedi i twin',
      open: 'Apri la conversazione',
      twins: { one: '{count} twin', other: '{count} twin' },
      rate: { one: 'Invia questa domanda. {count} credito per messaggio.', other: 'Invia questa domanda. {count} crediti per messaggio.' },
      noTwins: 'Nessun twin corrisponde a questa audience.',
      startFailed: 'Impossibile avviare questa conversazione. Riprova.',
      roomsUnavailable: 'Impossibile verificare questa conversazione. Aggiorna la pagina per riprovare.',
    },
    working: 'Ci sto lavorando\u2026',
    searching: 'Ricerca {targets}\u2026',
    searchingFor: 'Ricerca {targets} per "{query}"\u2026',
    searchingLead: 'Ricerca',
    composing: 'Sto scrivendo la risposta\u2026',
    preparingCanvas: 'Preparazione della tela visiva\u2026',
    runStarting: 'Avvio\u2026',
    runPreparing: 'Preparazione delle domande\u2026',
    runSampling: 'Sto chiedendo ai tuoi rispondenti\u2026',
    runWriting: 'Sto scrivendo\u2026',
    runPosting: 'Sto pubblicando\u2026',
    targets: {
      analyze_results: 'Analisi dei risultati',
      prepare_quick_study: 'Studio rapido',
      revise_run_step: 'Modifiche allo studio',
      propose_social_tracker: 'Ascolto social',
      read_room: 'Stanza',
      read_study: 'Studio',
      ask_study: 'Analisi dello studio',
      read_document: 'Documento',
      list_datasets: 'Dati',
      read_tracker_posts: 'Post social',
      analyze_table: 'Analisi tabella',
      report_reading: 'Fonti del report',
      report_writing: 'Scrittura del report',
      report_checking: 'Revisione del report',
      document_search: 'nei tuoi documenti',
      web_search: 'nel web',
      read_skill: 'nelle tue skill',
      read_workflow: 'nei tuoi flussi',
      read_audience: 'nelle tue audience',
      analyze_audience: 'nell\u2019analisi audience',
      read_results: 'nei tuoi risultati',
      read_artifact: 'nell\u2019artefatto',
      update_artifact: 'nell\u2019artefatto',
      create_artifact: 'nell\u2019artefatto creativo',
      delete_artifact: 'nell\u2019artefatto',
      run_workflow: 'un flusso',
      research_search: 'nei tuoi studi',
      analyze_tracker_posts: 'nei post dei tuoi tracker',
      generate_image: 'la tua immagine',
      write_report: 'Scrittura del report',
      prepare_study: 'uno studio',
      search_connected_sources: 'record connessi',
      call_connected_source: 'un record connesso',
      read_social_topic: 'Temi social',
      analyze_social_topic: 'nel tuo tema social',
      search_social_posts: 'Post social',
      read_social_post: 'Post social',
      prepare_conversation: 'una conversazione',
      tracker_search: 'nei tuoi tracker',
    },
    chips: {
      analyze_results: 'Analisi dei risultati',
      prepare_quick_study: 'Studio rapido',
      revise_run_step: 'Modifiche allo studio',
      propose_social_tracker: 'Ascolto social',
      read_room: 'Stanza',
      read_study: 'Studio',
      ask_study: 'Analisi dello studio',
      read_document: 'Documento',
      list_datasets: 'Dati',
      read_tracker_posts: 'Post social',
      analyze_table: 'Analisi tabella',
      report_reading: 'Fonti del report',
      report_writing: 'Scrittura del report',
      report_checking: 'Revisione del report',
      document_search: 'Libreria',
      web_search: 'Web',
      read_skill: 'Skill',
      read_workflow: 'Flussi',
      read_audience: 'Audience',
      analyze_audience: 'Analisi',
      read_results: 'Risultati',
      read_artifact: 'Artefatto',
      update_artifact: 'Artefatto',
      create_artifact: 'Creativi',
      delete_artifact: 'Artefatto',
      run_workflow: 'Flusso',
      research_search: 'Studi',
      analyze_tracker_posts: 'Post dei tracker',
      generate_image: 'Immagine',
      write_report: 'Scrittura del report',
      prepare_study: 'Studio',
      search_connected_sources: 'Record connessi',
      call_connected_source: 'Record connesso',
      read_social_topic: 'Temi social',
      analyze_social_topic: 'Tema social',
      search_social_posts: 'Post social',
      read_social_post: 'Post social',
      prepare_conversation: 'Conversazione',
      tracker_search: 'Tracker',
    },
  },
  nl: {
    askNext: 'Stel de volgende vraag\u2026',
    askChanges: 'Vraag om wijzigingen\u2026',
    steps: THREAD_STEPS_NL,
    conversationLaunch: {
      start: 'Starten',
      review: 'Twins bekijken',
      open: 'Gesprek openen',
      twins: { one: '{count} twin', other: '{count} twins' },
      rate: { one: 'Verstuurt deze vraag. {count} credit per bericht.', other: 'Verstuurt deze vraag. {count} credits per bericht.' },
      noTwins: 'Geen enkele twin past bij dit publiek.',
      startFailed: 'Het gesprek kon niet worden gestart. Probeer het opnieuw.',
      roomsUnavailable: 'Het gesprek kon niet worden gecontroleerd. Vernieuw de pagina om het opnieuw te proberen.',
    },
    working: 'Bezig\u2026',
    searching: 'Zoeken {targets}\u2026',
    searchingFor: 'Zoeken {targets} naar "{query}"\u2026',
    searchingLead: 'Zoeken',
    composing: 'Het antwoord wordt geschreven\u2026',
    preparingCanvas: 'Visueel canvas voorbereiden\u2026',
    runStarting: 'Bezig met starten\u2026',
    runPreparing: 'Uw vragen worden voorbereid\u2026',
    runSampling: 'Uw respondenten worden ondervraagd\u2026',
    runWriting: 'Bezig met schrijven\u2026',
    runPosting: 'Bezig met plaatsen\u2026',
    targets: {
      analyze_results: 'Resultaatanalyse',
      prepare_quick_study: 'Snelle studie',
      revise_run_step: 'Studiewijzigingen',
      propose_social_tracker: 'Social listening',
      read_room: 'Kamer',
      read_study: 'Studie',
      ask_study: 'Studieanalyse',
      read_document: 'Document',
      list_datasets: 'Gegevens',
      read_tracker_posts: 'Sociale berichten',
      analyze_table: 'Tabelanalyse',
      report_reading: 'Rapportbronnen',
      report_writing: 'Rapport schrijven',
      report_checking: 'Rapport controleren',
      document_search: 'in uw documenten',
      web_search: 'op het web',
      read_skill: 'in uw skills',
      read_workflow: 'in uw workflows',
      read_audience: 'in uw audiences',
      analyze_audience: 'in uw audience-analyse',
      read_results: 'in uw resultaten',
      read_artifact: 'in het artefact',
      update_artifact: 'in het artefact',
      create_artifact: 'in het creatives-artefact',
      delete_artifact: 'in het artefact',
      run_workflow: 'een workflow',
      research_search: 'in uw onderzoek',
      analyze_tracker_posts: 'in uw tracker-posts',
      generate_image: 'uw afbeelding',
      write_report: 'Rapport schrijven',
      prepare_study: 'een studie',
      search_connected_sources: 'verbonden records',
      call_connected_source: 'een verbonden record',
      read_social_topic: 'Social topics',
      analyze_social_topic: 'in je social topic',
      search_social_posts: 'Social posts',
      read_social_post: 'Social post',
      prepare_conversation: 'een gesprek',
      tracker_search: 'in uw trackers',
    },
    chips: {
      analyze_results: 'Resultaatanalyse',
      prepare_quick_study: 'Snelle studie',
      revise_run_step: 'Studiewijzigingen',
      propose_social_tracker: 'Social listening',
      read_room: 'Kamer',
      read_study: 'Studie',
      ask_study: 'Studieanalyse',
      read_document: 'Document',
      list_datasets: 'Gegevens',
      read_tracker_posts: 'Sociale berichten',
      analyze_table: 'Tabelanalyse',
      report_reading: 'Rapportbronnen',
      report_writing: 'Rapport schrijven',
      report_checking: 'Rapport controleren',
      document_search: 'Bibliotheek',
      web_search: 'Web',
      read_skill: 'Skills',
      read_workflow: 'Workflows',
      read_audience: 'Audience',
      analyze_audience: 'Analyse',
      read_results: 'Resultaten',
      read_artifact: 'Artefact',
      update_artifact: 'Artefact',
      create_artifact: 'Creatives',
      delete_artifact: 'Artefact',
      run_workflow: 'Workflow',
      research_search: 'Onderzoek',
      analyze_tracker_posts: 'Tracker-posts',
      generate_image: 'Afbeelding',
      write_report: 'Rapport schrijven',
      prepare_study: 'Studie',
      search_connected_sources: 'Verbonden records',
      call_connected_source: 'Verbonden record',
      read_social_topic: 'Social topics',
      analyze_social_topic: 'Social topic',
      search_social_posts: 'Social posts',
      read_social_post: 'Social post',
      prepare_conversation: 'Gesprek',
      tracker_search: 'Trackers',
    },
  },
  tr: {
    askNext: 'Sonra ne sormak istersiniz\u2026',
    askChanges: 'De\u011fi\u015fiklik isteyin\u2026',
    steps: THREAD_STEPS_TR,
    conversationLaunch: {
      start: 'Ba\u015flat',
      review: 'Twin\u2019leri incele',
      open: 'G\u00f6r\u00fc\u015fmeyi a\u00e7',
      twins: { other: '{count} twin' },
      rate: { other: 'Bu soruyu g\u00f6nderir. Mesaj ba\u015f\u0131na {count} kredi.' },
      noTwins: 'Bu hedef kitleyle e\u015fle\u015fen twin yok.',
      startFailed: 'G\u00f6r\u00fc\u015fme ba\u015flat\u0131lamad\u0131. L\u00fctfen tekrar deneyin.',
      roomsUnavailable: 'G\u00f6r\u00fc\u015fme kontrol edilemedi. Tekrar denemek i\u00e7in sayfay\u0131 yenileyin.',
    },
    working: '\u00dczerinde \u00e7al\u0131\u015f\u0131l\u0131yor\u2026',
    searching: '{targets} aran\u0131yor\u2026',
    searchingFor: '{targets} i\u00e7inde "{query}" aran\u0131yor\u2026',
    searchingLead: 'Aran\u0131yor',
    composing: 'Yan\u0131t yaz\u0131l\u0131yor\u2026',
    preparingCanvas: 'G\u00f6rsel tuval haz\u0131rlan\u0131yor\u2026',
    runStarting: 'Ba\u015flat\u0131l\u0131yor\u2026',
    runPreparing: 'Sorular\u0131n\u0131z haz\u0131rlan\u0131yor\u2026',
    runSampling: 'Kat\u0131l\u0131mc\u0131lar\u0131n\u0131za soruluyor\u2026',
    runWriting: 'Yaz\u0131l\u0131yor\u2026',
    runPosting: 'G\u00f6nderiliyor\u2026',
    targets: {
      analyze_results: 'Sonuç analizi',
      prepare_quick_study: 'Hızlı çalışma',
      revise_run_step: 'Çalışma değişiklikleri',
      propose_social_tracker: 'Sosyal dinleme',
      read_room: 'Oda',
      read_study: 'Çalışma',
      ask_study: 'Çalışma analizi',
      read_document: 'Belge',
      list_datasets: 'Veri kümeleri',
      read_tracker_posts: 'Sosyal gönderiler',
      analyze_table: 'Tablo analizi',
      report_reading: 'Rapor kaynakları',
      report_writing: 'Rapor yazımı',
      report_checking: 'Rapor incelemesi',
      document_search: 'Belgeleriniz',
      web_search: 'Web',
      read_skill: 'Becerileriniz',
      read_workflow: 'I\u015f ak\u0131\u015flar\u0131n\u0131z',
      read_audience: 'Kitleleriniz',
      analyze_audience: 'Kitle analiziniz',
      read_results: 'Sonu\u00e7lar\u0131n\u0131z',
      read_artifact: '\u00c7\u0131kt\u0131n\u0131z',
      update_artifact: '\u00c7\u0131kt\u0131n\u0131z',
      create_artifact: 'kreatif \u00e7\u0131kt\u0131n\u0131z',
      delete_artifact: '\u00c7\u0131kt\u0131n\u0131z',
      run_workflow: 'bir i\u015f ak\u0131\u015f\u0131',
      research_search: 'Araştırmalarınız',
      analyze_tracker_posts: 'Tracker gönderileriniz',
      generate_image: 'Görseliniz',
      write_report: 'Rapor yazımı',
      prepare_study: 'bir çalışma',
      search_connected_sources: 'bağlı kayıtlar',
      call_connected_source: 'bir bağlı kayıt',
      read_social_topic: 'Sosyal konular',
      analyze_social_topic: 'sosyal konunuzda',
      search_social_posts: 'Sosyal gönderiler',
      read_social_post: 'Sosyal gönderi',
      prepare_conversation: 'bir sohbet',
      tracker_search: 'Tracker\'larınız',
    },
    chips: {
      analyze_results: 'Sonuç analizi',
      prepare_quick_study: 'Hızlı çalışma',
      revise_run_step: 'Çalışma değişiklikleri',
      propose_social_tracker: 'Sosyal dinleme',
      read_room: 'Oda',
      read_study: 'Çalışma',
      ask_study: 'Çalışma analizi',
      read_document: 'Belge',
      list_datasets: 'Veri kümeleri',
      read_tracker_posts: 'Sosyal gönderiler',
      analyze_table: 'Tablo analizi',
      report_reading: 'Rapor kaynakları',
      report_writing: 'Rapor yazımı',
      report_checking: 'Rapor incelemesi',
      document_search: 'Kitapl\u0131k',
      web_search: 'Web',
      read_skill: 'Beceriler',
      read_workflow: 'I\u015f ak\u0131\u015flar\u0131',
      read_audience: 'Kitle',
      analyze_audience: 'Analiz',
      read_results: 'Sonu\u00e7lar',
      read_artifact: '\u00c7\u0131kt\u0131',
      update_artifact: '\u00c7\u0131kt\u0131',
      create_artifact: 'Kreatifler',
      delete_artifact: '\u00c7\u0131kt\u0131',
      run_workflow: 'I\u015f ak\u0131\u015f\u0131',
      research_search: 'Araştırma',
      analyze_tracker_posts: 'Tracker gönderileri',
      generate_image: 'Görsel',
      write_report: 'Rapor yazımı',
      prepare_study: 'Çalışma',
      search_connected_sources: 'Bağlı kayıtlar',
      call_connected_source: 'Bağlı kayıt',
      read_social_topic: 'Sosyal konular',
      analyze_social_topic: 'Sosyal konu',
      search_social_posts: 'Sosyal gönderiler',
      read_social_post: 'Sosyal gönderi',
      prepare_conversation: 'Sohbet',
      tracker_search: 'Tracker\'lar',
    },
  },
  ja: {
    askNext: '\u7d9a\u3051\u3066\u8cea\u554f\u3059\u308b\u2026',
    askChanges: '\u5909\u66f4\u3092\u30ea\u30af\u30a8\u30b9\u30c8\u2026',
    steps: THREAD_STEPS_JA,
    conversationLaunch: {
      start: '\u958b\u59cb',
      review: 'twin\u3092\u78ba\u8a8d',
      open: '\u4f1a\u8a71\u3092\u958b\u304f',
      twins: { other: 'twin {count}\u4eba' },
      rate: { other: '\u3053\u306e\u8cea\u554f\u3092\u9001\u4fe1\u3057\u307e\u3059\u30021\u30e1\u30c3\u30bb\u30fc\u30b8\u3042\u305f\u308a{count}\u30af\u30ec\u30b8\u30c3\u30c8\u3002' },
      noTwins: '\u3053\u306e\u30aa\u30fc\u30c7\u30a3\u30a8\u30f3\u30b9\u306b\u4e00\u81f4\u3059\u308btwin\u304c\u3042\u308a\u307e\u305b\u3093\u3002',
      startFailed: '\u4f1a\u8a71\u3092\u958b\u59cb\u3067\u304d\u307e\u305b\u3093\u3067\u3057\u305f\u3002\u3082\u3046\u4e00\u5ea6\u304a\u8a66\u3057\u304f\u3060\u3055\u3044\u3002',
      roomsUnavailable: '\u4f1a\u8a71\u3092\u78ba\u8a8d\u3067\u304d\u307e\u305b\u3093\u3067\u3057\u305f\u3002\u30da\u30fc\u30b8\u3092\u518d\u8aad\u307f\u8fbc\u307f\u3057\u3066\u3082\u3046\u4e00\u5ea6\u304a\u8a66\u3057\u304f\u3060\u3055\u3044\u3002',
    },
    working: '\u51e6\u7406\u3057\u3066\u3044\u307e\u3059\u2026',
    searching: '{targets}\u3092\u691c\u7d22\u3057\u3066\u3044\u307e\u3059\u2026',
    searchingFor:
      '{targets}\u3067\u300c{query}\u300d\u3092\u691c\u7d22\u3057\u3066\u3044\u307e\u3059\u2026',
    searchingLead: '\u691c\u7d22\u4e2d',
    composing: '\u56de\u7b54\u3092\u4f5c\u6210\u3057\u3066\u3044\u307e\u3059\u2026',
    preparingCanvas: '\u30d3\u30b8\u30e5\u30a2\u30eb\u30ad\u30e3\u30f3\u30d0\u30b9\u3092\u6e96\u5099\u3057\u3066\u3044\u307e\u3059\u2026',
    runStarting: '\u958b\u59cb\u3057\u3066\u3044\u307e\u3059\u2026',
    runPreparing: '\u8cea\u554f\u3092\u6e96\u5099\u3057\u3066\u3044\u307e\u3059\u2026',
    runSampling: '\u56de\u7b54\u8005\u306b\u8cea\u554f\u3057\u3066\u3044\u307e\u3059\u2026',
    runWriting: '\u4f5c\u6210\u3057\u3066\u3044\u307e\u3059\u2026',
    runPosting: '\u6295\u7a3f\u3057\u3066\u3044\u307e\u3059\u2026',
    targets: {
      analyze_results: '結果の分析',
      prepare_quick_study: 'クイック調査',
      revise_run_step: '調査の変更',
      propose_social_tracker: 'ソーシャルリスニング',
      read_room: 'ルーム',
      read_study: '調査',
      ask_study: '調査の分析',
      read_document: 'ドキュメント',
      list_datasets: 'データセット',
      read_tracker_posts: 'ソーシャル投稿',
      analyze_table: '表の分析',
      report_reading: 'レポートの情報源',
      report_writing: 'レポートの作成',
      report_checking: 'レポートの確認',
      document_search: '\u30c9\u30ad\u30e5\u30e1\u30f3\u30c8',
      web_search: '\u30a6\u30a7\u30d6',
      read_skill: '\u30b9\u30ad\u30eb',
      read_workflow: '\u30ef\u30fc\u30af\u30d5\u30ed\u30fc',
      read_audience: '\u30aa\u30fc\u30c7\u30a3\u30a8\u30f3\u30b9',
      analyze_audience: '\u30aa\u30fc\u30c7\u30a3\u30a8\u30f3\u30b9\u5206\u6790',
      read_results: '\u7d50\u679c',
      read_artifact: '\u30a2\u30fc\u30c6\u30a3\u30d5\u30a1\u30af\u30c8',
      update_artifact: '\u30a2\u30fc\u30c6\u30a3\u30d5\u30a1\u30af\u30c8',
      create_artifact: '\u30af\u30ea\u30a8\u30a4\u30c6\u30a3\u30d6',
      delete_artifact: '\u30a2\u30fc\u30c6\u30a3\u30d5\u30a1\u30af\u30c8',
      run_workflow: '\u30ef\u30fc\u30af\u30d5\u30ed\u30fc',
      research_search: 'リサーチ',
      analyze_tracker_posts: 'トラッカーの投稿',
      generate_image: '画像',
      write_report: 'レポートの作成',
      prepare_study: '調査',
      search_connected_sources: '接続済みのレコード',
      call_connected_source: '接続済みのレコード',
      read_social_topic: 'ソーシャルトピック',
      analyze_social_topic: 'ソーシャルトピック',
      search_social_posts: 'ソーシャル投稿',
      read_social_post: 'ソーシャル投稿',
      prepare_conversation: '会話',
      tracker_search: 'トラッカー',
    },
    chips: {
      analyze_results: '結果の分析',
      prepare_quick_study: 'クイック調査',
      revise_run_step: '調査の変更',
      propose_social_tracker: 'ソーシャルリスニング',
      read_room: 'ルーム',
      read_study: '調査',
      ask_study: '調査の分析',
      read_document: 'ドキュメント',
      list_datasets: 'データセット',
      read_tracker_posts: 'ソーシャル投稿',
      analyze_table: '表の分析',
      report_reading: 'レポートの情報源',
      report_writing: 'レポートの作成',
      report_checking: 'レポートの確認',
      document_search: '\u30e9\u30a4\u30d6\u30e9\u30ea',
      web_search: 'Web',
      read_skill: '\u30b9\u30ad\u30eb',
      read_workflow: '\u30ef\u30fc\u30af\u30d5\u30ed\u30fc',
      read_audience: '\u30aa\u30fc\u30c7\u30a3\u30a8\u30f3\u30b9',
      analyze_audience: '\u5206\u6790',
      read_results: '\u7d50\u679c',
      read_artifact: '\u30a2\u30fc\u30c6\u30a3\u30d5\u30a1\u30af\u30c8',
      update_artifact: '\u30a2\u30fc\u30c6\u30a3\u30d5\u30a1\u30af\u30c8',
      create_artifact: '\u30af\u30ea\u30a8\u30a4\u30c6\u30a3\u30d6',
      delete_artifact: '\u30a2\u30fc\u30c6\u30a3\u30d5\u30a1\u30af\u30c8',
      run_workflow: '\u30ef\u30fc\u30af\u30d5\u30ed\u30fc',
      research_search: 'リサーチ',
      analyze_tracker_posts: 'トラッカーの投稿',
      generate_image: '画像',
      write_report: 'レポートの作成',
      prepare_study: '調査',
      search_connected_sources: '接続済みのレコード',
      call_connected_source: '接続済みのレコード',
      read_social_topic: 'ソーシャルトピック',
      analyze_social_topic: 'ソーシャルトピック',
      search_social_posts: 'ソーシャル投稿',
      read_social_post: 'ソーシャル投稿',
      prepare_conversation: '会話',
      tracker_search: 'トラッカー',
    },
  },
}

/**
 * The table key a thread's language actually resolves to, English for anything
 * absent.
 *
 * Exported because the waiting line needs it twice over: once to pick the copy,
 * and once more to join the target fragments, which has to happen in the SAME
 * language the fragments came from. Resolving separately in the caller is how a
 * Korean thread would get English fragments joined by a Korean conjunction.
 */
export function resolveThreadLanguage(language?: string | null): string {
  return language && THREAD_PLACEHOLDERS[language] ? language : 'en'
}

export function getThreadPlaceholders(language?: string | null): ThreadPlaceholderTemplates {
  return THREAD_PLACEHOLDERS[resolveThreadLanguage(language)]
}
